import {
  faGlobe,
  faUsers,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";

export const TEMPLATE_TYPES = {
  SOAP: "SOAP",
  CUSTOM: "CUSTOM",
};

export const SHARE_OPTIONS = [
  {
    value: "PRIVATE",
    title: "Restricted",
    description: "Only you and people invited via email have access",
    icon: faUser,
  },
  {
    value: "TEAM",
    title: "My Team",
    description: "Share with your team members automatically",
    icon: faUsers,
  },
  {
    value: "PUBLIC",
    title: "Public (Anyone with the link)",
    description: "Share with anyone using a link",
    icon: faGlobe,
  },
];

export const GROUP_SHARE_OPTIONS = [
  {
    value: "PRIVATE",
    title: "Private",
    description: "Only you can use this template group",
    icon: faUser,
  },
  {
    value: "TEAM",
    title: "My Team",
    description: "Share with your team members",
    icon: faUsers,
  },
];

export const TEMPLATE_FIELDS = {
  SUBJECTIVE: "SUBJECTIVE",
  OBJECTIVE: "OBJECTIVE",
  ASSESSMENT: "ASSESSMENT",
  PLAN: "PLAN",
  CUSTOM: "CUSTOM",
};

export const SOAP_FIELDS = [
  {
    fieldName: TEMPLATE_FIELDS.SUBJECTIVE,
    fieldIdx: 0,
    fieldValue: "",
  },
  {
    fieldName: TEMPLATE_FIELDS.OBJECTIVE,
    fieldIdx: 1,
    fieldValue: "",
  },
  {
    fieldName: TEMPLATE_FIELDS.ASSESSMENT,
    fieldIdx: 2,
    fieldValue: "",
  },
  { fieldName: TEMPLATE_FIELDS.PLAN, fieldIdx: 3, fieldValue: "" },
];

export const CUSTOM_FIELDS = [
  { fieldName: TEMPLATE_FIELDS.CUSTOM, fieldIdx: 0, fieldValue: "" },
];

export const DEFAULT_TEMPLATE_EXAMPLES = {
  [TEMPLATE_TYPES.SOAP]: {
    name: "Eg: Structured Diagnostic Discussion",
    description:
      "Eg: A structured SOAP template ready to accommodate your noteworthy notetaking style.",
  },
  [TEMPLATE_TYPES.CUSTOM]: {
    name: "Eg: Freeform Template",
    description:
      "A freeform template allows you to create a template with no predefined fields. You can add your own fields and customize the template to your needs.",
  },
};

export const FIELD_STYLES = {
  [TEMPLATE_FIELDS.SUBJECTIVE]: {
    container:
      "bg-linear-to-b dark:from-blue-900/50 from-blue-100 to-transparent border border-blue-200 dark:border-blue-800/50",
    text: "text-blue-400 dark:text-blue-500",
    placeholder: `Describe what kind of details you want to capture here,  e.g., presenting complaint, history, behavior changes, eating or drinking habits, or previous medical history.
\nOutline any structural preferences you have, such as bullet points, subheaders, paragraph form, etc.
\nExample: 
Write a detailed history, including the signalment of the patient, in paragraph form, and list any current medications below in bullet points.`,
    simplePlaceholder:
      "Select one of the example chips above, or start typing here!",
  },
  [TEMPLATE_FIELDS.OBJECTIVE]: {
    container:
      "bg-linear-to-b dark:from-pink-900/50 from-pink-100 to-transparent border border-pink-200 dark:border-pink-800/50",
    text: "text-pink-400 dark:text-pink-500",
    placeholder: `List the types of clinical findings and exam elements that should be recorded here — vitals, physical exam systems, lab or imaging results. 
\nFill with the default “normal” words you want Scribenote to use if the system is not abnormal. 
 
Example: 
Cardiovascular: Regular sinus rhythm; no murmur detected, strong pulses.
Lymph nodes: Soft, small, pliable
Urogenital: Normal on exam
Neurologic: Appropriate mentation
Rectal: Not examined
...`,
    simplePlaceholder:
      "Select one of the example chips above, or start typing here!",
  },
  [TEMPLATE_FIELDS.ASSESSMENT]: {
    container:
      "bg-linear-to-b dark:from-green-900/50 from-green-100 to-transparent border border-green-200 dark:border-green-800/50",
    text: "text-green-400 dark:text-green-500",
    placeholder: `Write guidance for how this section should summarize medical impressions, differential diagnoses, or case status.

Example: 
Summarize clinical impressions or possible causes in short bullet points.`,
    simplePlaceholder:
      "Select one of the example chips above, or start typing here!",
  },
  [TEMPLATE_FIELDS.PLAN]: {
    container:
      "bg-linear-to-b dark:from-yellow-900/50 from-yellow-100 to-transparent border border-yellow-200 dark:border-yellow-800/50",
    text: "text-yellow-400 dark:text-yellow-500",
    placeholder: `Describe what to include here — treatments, diagnostics, follow-up, or client communication. You can ask for follow-up documents here, such as discharges or follow-up emails.

Examples: 
List recommended treatments or tests, and note if the owner declined.
Include a friendly follow-up email containing personalized information from our conversation, including home care instructions and detailed next steps. `,
    simplePlaceholder:
      "Select one of the example chips above, or start typing here!",
  },
  [TEMPLATE_FIELDS.CUSTOM]: {
    container: "bg-gray-100 dark:bg-gray-700",
    text: "text-gray-400",
    placeholder:
      "Enter template here, and format it to your heart's content. If you're not sure what to include, try using the samples above.",
    simplePlaceholder:
      "Select one of the example chips above, or start typing here!",
  },
};

// ==================== SOAP TEMPLATE EXAMPLES ====================

const DEFAULT_SOAP_TEMPLATE_EXAMPLE = {
  name: "Medical Record - Customizable",
  [TEMPLATE_FIELDS.SUBJECTIVE]: "",
  [TEMPLATE_FIELDS.OBJECTIVE]: ` 
Temperature: normal
Heart Rate: within normal limits
Weight: list in kg
Respiratory Rate: within normal limits
Capillary Refill Time: moist, <2 seconds
Mucous Membranes: pink
Hydration Status: normal hydration
Body Condition Score: 5/9 
Pain Score: 0
Fear Anxiety Stress Score: Green
Eyes: no clouding, no debris, sclera is clear
Ears: no debris or erythema
Nasal: normal
Oral/Dental: healthy teeth and gums
Cardiovascular: no murmur or arrhythmia, strong femoral pulses
Respiratory: lung sounds are clear, no crackles or wheezes
Neurological: normal mentation
Musculoskeletal: good range of motion, no gait deficits, normal muscling 
Lymphatic: small, soft, pliable
Gastrointestinal: comfortable on palpation, no mass effect, no fluid wave
Reproductive/Urinary: normal conformation, no discharge
Integument: healthy skin and coat, no evidence of ectoparasites
Masses: no lumps or bumps
`,
  [TEMPLATE_FIELDS.ASSESSMENT]: "",
  [TEMPLATE_FIELDS.PLAN]: "",
  preferences: `Write the Subjective in paragraph format. Use Fahrenheit for temperature. List the size, location and description for all masses. Write the Assessment in bullet points (use dashes) without subheaders; be brief and concise. Write the plan in bullet points (use dashes) without subheaders; be specific. Include all potential courses of treatments discussed with costs, even if declined (note if declined). If vaccines are noted, note locations: e.g. Rabies RH SQ 3y, DHPP/Lepto LH SQ 3y/1y, Lepto LH SQ 1y (if alone), Bord orally. Use medical terminology where possible.`,
  sampleTranscript: `Daisy 4y/o mn sheepdog cross. No health concerns; no vomiting, diarrhea, coughing, or sneezing.
Exam: Normal except for moderate tartar buildup on the premolars and body condition score of 8/9
Vaccines given: Rabies and DHPP/Lepto.
Owners declined Bordetella vaccine.
Discharged with 3 months of Simparica Trio.
Advised regular dental care via teeth brushing or CET dental chews for tartar control.`,
};

const ADULT_WELLNESS_SOAP_TEMPLATE_EXAMPLE = {
  name: "Adult Wellness - Vaccine",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `Pet information:
Any C/S/V/D?:
Any health concerns:
Diet:
Flea/tick/heartworm medication:
Other medications:
Previous vaccine reactions?:

[answer the question without restarting the question - ie, instead of "Any C/S/V/D?: No", write "No C/S/V/D" for that line.]
`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `General Appearance: BAR
Fear Anxiety Stress Score: Green
Weight: - kg
Temperature: Not taken
HR: WNL
RR: WNL
BCS: 5/9
Eyes: clear
Ears: No debris or erythema
Oral Cavity: healthy teeth and gums
Cardiovascular: Regular rhythm; no murmur detected, strong pulses.
Respiratory: Lungs auscultate clear bilaterally. 
Abdomen: Abdomen palpates normally
Musculoskeletal: Normal gait
Integument: healthy skin and coat
Lymph nodes: soft, small, pliable
Urogenital: normal on exam
Neurologic: Normal mentation`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Overall:`,
  [TEMPLATE_FIELDS.PLAN]: `Vaccines: 

Prevention sent home: 

Follow up:`,
  preferences: `Write in point form. 
For dog vaccines if given denote Rabies RH SQ 3y, DHPP/Lepto combo LH SQ 3y/1y 
Oral Bordetella
For cat vaccines if given denote Rabies RH SQ 3y, FVRCP LH SQ 3y, FLV LF SQ 1y
`,
  sampleTranscript: `Dobby 4y/o mn sheepdog cross. No health concerns. No V/D/C/S. All Exam normal except tartar on PM4's. Gave Rabies, DHPP/Lepto. Owners declined Bordetella vaccine
Send home 3 months of Simparica Trio. Recommend Teeth brushing or using CET dental chews for dental tartar.`,
};

const MY_LUMPS_AND_BUMPS_SOAP_TEMPLATE_EXAMPLE = {
  name: "Lumps and Bumps",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `History:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `Weight: - kg
Temperature: Not taken
HR: WNL
RR: WNL
BCS: 5 out of 9
General Appearance: Bright, alert and responsive
Hydration: Hydration appears normal
Eyes: no abnormalities
Ears: No debris or erythema
Oral Cavity: healthy teeth and gums
Cardiovascular: Regular rhythm; no murmur detected
Respiratory: Lungs auscultate clear bilaterally. 
Abdomen: Abdomen palpates normally; no pain, tenderness or internal masses on palpation
Musculoskeletal: Normal ambulation, Normal ROM
Integument: skin looks normal; hair coat in good condition. No ectoparasites. No masses found.
Lymph nodes: Lymph nodes are soft and small
Urogenital: External genitalia appears normal; bladder palpates normally
Neurologic: Normal mentation
Masses: List any masses with locations and size descriptions here`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Problem List:
Differentials:`,
  [TEMPLATE_FIELDS.PLAN]: `Diagnostics performed: 

Plan/ Follow up:`,
  preferences: `Bring abnormal findings to the top of the objective section.`,
  sampleTranscript: `Shelley is a 9 y/o female spayed Weimaraner. Presented for assessment and update of all masses present and measurements. Shelly has moderate age related changes in both eyes. There is moderate dental disease present with moderate tartar and gingivitis present. Lymph nodes are all small and soft. There are multiple masses. Five masses that we'll measure today. The first is on the right scapula. It is raised and round, measuring 2.3 by 3.5 cm. It is dermal and firm. There is a large mass on the left lateral abdomen that measures 5cm by 5cm. It is soft and subcutaneous. There is a mass located on the caudal aspect of the left hind limb at the distal aspect of the limb. It measures 1 x 1 cm. There is a firm mass on the occipital crest of the head. It measures 0.5 by 0.7 cm. And the last mass is on the right front foot on the dorsal aspect. It is a small skin tag that does not have a deep attachment and is 1cm long. All these masses can continue to be monitored. If any grow, change rapidly or become bothersome, they can be removed under general sedation.
Owner declines any FNA samples and wishes to measure and monitor masses at this time
  `,
};

const MY_GENERAL_CONSULT_SOAP_TEMPLATE_EXAMPLE = {
  name: "General Consult",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `Pet's signalment: (Name, Age, breed, sex, if spayed or neutered)
History: 
Diet: 
HW/ flea + tick preventative:
Current Medications/ supplements:
Any vaccine or medication reactions:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `Weight: - kg
Temperature: Not taken
HR: WNL
RR: WNL
BCS: 5 out of 9
FAS: Green
Hydration: Hydration appears normal
Eyes: no clouding, no debris, sclera is clear
Ears: No debris or erythema
Oral Cavity: healthy teeth and gums
Cardiovascular: no murmur or arrhythmia, strong pulses.
Respiratory: Lungs sounds are clear, no crackles or wheezes
Abdomen: comfortable on palpation, no mass effect, no fluid wave
Musculoskeletal: Normal ambulation, Normal ROM
Integument: skin looks normal; hair coat in good condition. No ectoparasites. No masses found.
Lymph nodes: Lymph nodes are soft and small
Urogenital: External genitalia appears normal; bladder palpates normally
Neurologic: Normal mentation`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Problem List:
Differentials: If no differentials note stable on exam`,
  [TEMPLATE_FIELDS.PLAN]: `Treatments: 
Diagnostics Completed: 
Prescriptions:  
Follow up:`,
  preferences: `Write in medical terminology, write in point form`,
  sampleTranscript: `Scribbles is a 15 year old FS DLH presenting for a senior wellness exam. Owner has concerns about difficulty jumping and vocalization at night. Lenticular sclerosis with moderate cataracts OU. Marked dental disease with halitosis. Vocalization may be due to cognitive changes, recommend giving gabapentin 100mg at bed time to help with wandering. Follow up with weigh in in 1 month, consider Senior bloodwork if continued muscle wasting or weight loss noted. 
  `,
};

const MY_RECHECK_SOAP_TEMPLATE_EXAMPLE = {
  name: "Recheck",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `History: 
When last examined: 
Any noted changes since:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `List the body system and findings that are discussed only. List body systems on separate lines.`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `General:`,
  [TEMPLATE_FIELDS.PLAN]: `Next Steps:`,
  preferences: `Use dashes. Be concise`,
  sampleTranscript: `Gizmo presented for recheck from 2 weeks ago. Has been doing very well on Fibre diet and lactulose was discontinued 1 week ago. Normal bowel movements daily. Hydration is normal. Abdominal palpation reveals soft and comfortable abdomen with no firm stool palpated. Overall improvement is noted. Recommend to continue Royal Canin Fibre diet dry and continue to monitor bowel movements in litter box.`,
};

const ALLERGY_APPOINTMENT_SOAP_TEMPLATE_EXAMPLE = {
  name: "Allergy",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `Where is the patient scratching:
Is it all the time or seasonal:
When did that start:
Diet and treats: 
Topicals used:
Medications: 
Other noted historical concerns:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `Weight: - kg
Temperature: Not taken
HR: WNL
RR: WNL
BCS: 5 out of 9
FAS: Green
Hydration: Hydration appears normal
Eyes: no clouding, no debris, sclera is clear
Ears: No debris or erythema
Oral Cavity: healthy teeth and gums
Cardiovascular: no murmur or arrhythmia, strong pulses.
Respiratory: Lungs sounds are clear, no crackles or wheezes
Abdomen: comfortable on palpation, no mass effect, no fluid wave
Musculoskeletal: Normal ambulation, Normal ROM
Integument: No erythema or lichenification present, no ectoparasites
Lymph nodes: Lymph nodes are soft and small
Urogenital: External genitalia appears normal; bladder palpates normally
Neurologic: Normal mentation`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Problem list: 
Differential diagnosis:`,
  [TEMPLATE_FIELDS.PLAN]: `Cytology samples taken today: (yes/ no) and location
Topicals recommended:
Diet recommended:

Other follow up:`,
  preferences: `Be detailed when discussing subjective information and plan recommendations.`,
  sampleTranscript: `Cali is a 10 year old female spayed golden retriever. She has had allergies her whole life. They are all year long. She has had many ear infections and skin infections. She is currently on Apoquel and every other day ketoconazole. Her owner bathes her twice a week with prohex shampoo. Feet have been extremely raw and sore for 2 weeks. She has been licking alot. Front right foot has erythema and moist dermatitis interdigitally. Remainder of feet have moderate erythema on the ventral aspect. Cytology samples of the feet taken, Continue with prohex shampoo but increase frequency to daily for 5 minutes until improvement is noted. Recommend 12 week strict diet trial with Hypo HP diet (no treats or other snacks).`,
};
const DR_KATIES_GP_SOAP_TEMPLATE_EXAMPLE = {
  name: "Dr. Katie's GP SOAP",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `Animal Advocate: (Who is here with the pet at the appointment- list a name if mentioned)
Lifestyle: (If noted, otherwise remove category)
Pet's signalment: (List Pet's name, gender, species if mentioned)
History: (Do not return any direct quotations, Keep under 50 words, do not report exam findings)
Diet:
HW/ flea + tick preventative:
Current Medications/ supplements:
Any vaccine or medication reactions:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `(DO NOT REPORT ANY OWNER ANECDOTES in this section)
Temperature: not taken
Heart Rate: within normal limits
Weight: list in kg
Respiratory Rate: within normal limits
Capillary Refill Time: moist, <2 seconds
Mucous Membranes: pink
Hydration Status: normal hydration
Body Condition Score: 5/9
Pain Score: 0 (rate /4: 1 mild, 2-3 moderate, 4 severe- unable to touch/handle)
Fear Anxiety Stress Score: Green (Yellow if tension, nervous, anxious, Red if trying to bite)
Eyes: no clouding, no debris, sclera is clear
Ears: no debris or erythema
Nasal: normal
Oral/Dental: healthy teeth and gums
Cardiovascular: no murmur or arrhythmia, strong femoral pulses
Respiratory: lung sounds are clear, no crackles or wheezes
Neurological: normal mentation
Musculoskeletal: good range of motion, no gait deficits, normal muscling
Lymphatic: small, soft, pliable
Gastrointestinal: comfortable on palpation, no mass effect, no fluid wave
Reproductive/Urinary: normal conformation, no discharge
Integument: healthy skin and coat, no evidence of ectoparasites
Masses: no lumps or bumps noted`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Problem List: Remove category if none listed
Differentials: Remove category if none listed
If vaccines are completed please include the statement: Apparently healthy, stable for vaccines`,
  [TEMPLATE_FIELDS.PLAN]: `Vaccines: if given denote location: Rabies RH SQ 3y, DHPP 3y LH SQ, Lepto 1y LH SQ, Kennel Cough 1y given by mouth
Treatments: Injections/ medications applied or given orally during the appointment. Do not repeat vaccinations here.
Diagnostics Completed:
Prescriptions:
Client communication: (summarize in point form any topics reviewed with the owner, Keep under 50 words, do not repeat information from previous sections)
Follow up: Remove category if none specified`,
  preferences: `Use medical terminology.
Note if anything is declined.
Include costs if mentioned.
Write history and client communication in point form (use dashes).
Bold abnormals in objective section`,
  sampleTranscript:
    "Justin brought Otto a 4 year old MN golden retriever who lives in a kennel outdoors, for his annual exam. He is currently on year round Simparica Trio. There are no health concerns or issues with previous vaccine reactions. Otto weighs 28kg and is very friendly. His exam is normal. He received his Rabies, Lepto and Bordetella today. We obtained a 4dx sample from his RF cephalic and he will go home with 6 doses of Trio today. Discussed importance of continuing prevention on time due to high risk of heartworm and lyme in the area. Owner to bring a fecal exam and drop it off to rule out any internal parasites. ",
};

const DR_KATIES_MULTI_PET_SOAP_TEMPLATE_EXAMPLE = {
  name: "Multi-Pet - Customizable",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `
Pet's signalment: (Name, Age, breed, sex, if spayed or neutered)
History: (Do not return any direct quotations, point form, use dashes, keep under 50 words.)
Diet:
HW/ flea + tick preventative:
Current Medications/ supplements:
Any vaccine or medication reactions:

Pet's signalment: (Name, Age, breed, sex, if spayed or neutered)
History: (Do not return any direct quotations, point form, use dashes, keep under 50 words.)
Diet:
HW/ flea + tick preventative:
Current Medications/ supplements:
Any vaccine or medication reactions:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `(DO NOT REPORT ANY OWNER ANECDOTES in this section)
(Include Pet Name associated with this information)
Weight: - kg
Temperature: Not taken
HR: WNL
RR: WNL
BCS: 5 out of 9
FAS: Green
Hydration: Hydration appears normal
Eyes: no clouding, no debris, sclera is clear
Ears: No debris or erythema
Oral Cavity: healthy teeth and gums
Cardiovascular: no murmur or arrhythmia, strong pulses.
Respiratory: Lungs sounds are clear, no crackles or wheezes
Abdomen: comfortable on palpation, no mass effect, no fluid wave
Musculoskeletal: Normal ambulation, Normal ROM
Integument: skin looks normal; hair coat in good condition. No ectoparasites. No masses found.
Lymph nodes: Lymph nodes are soft and small
Urogenital: External genitalia appears normal; bladder palpates normally
Neurologic: Normal mentation

(Include Pet Name associated with this information)
Weight: - kg
Temperature: Not taken
HR: WNL
RR: WNL
BCS: 5 out of 9
FAS: Green
Hydration: Hydration appears normal
Eyes: no clouding, no debris, sclera is clear
Ears: No debris or erythema
Oral Cavity: healthy teeth and gums
Cardiovascular: no murmur or arrhythmia, strong pulses.
Respiratory: Lungs sounds are clear, no crackles or wheezes
Abdomen: comfortable on palpation, no mass effect, no fluid wave
Musculoskeletal: Normal ambulation, Normal ROM
Integument: skin looks normal; hair coat in good condition. No ectoparasites. No masses found.
Lymph nodes: Lymph nodes are soft and small
Urogenital: External genitalia appears normal; bladder palpates normally
Neurologic: Normal mentation`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `(Include Pet Name associated with this information)
Problem List:
Differentials: If no differentials note stable on exam

(Include Pet Name associated with this information)
Problem List:
Differentials: If no differentials note stable on exam`,
  [TEMPLATE_FIELDS.PLAN]: `(Include Pet Name associated with this information)
Treatments:
Diagnostics Completed:
Prescriptions:
Follow up:

(Include Pet Name associated with this information)
Treatments:
Diagnostics Completed:
Prescriptions:
Follow up:`,
  preferences: `Write in medical terminology. Fill out the template sections for each pet (there may be 2 or 3)`,
  sampleTranscript:
    "Otto and Scribbles are here for their annual exam and vaccines. There are no health concerns. Otto is a 4yo MN golden retriever, Scribbles is a 2 year old DSH who is indoor only. No health concerns, not on any medication. Otto is 30 kg Scribbles is 5kg. Exams are all wnl. Completed 4dx for Otto, sending home 6 doses of Trio, no vaccines due for either pet today, next year Otto due for 3y Rabies and DHPP, Scribbles due for 3y Rabies and FVRCP",
};
// ==================== END SOAP TEMPLATE EXAMPLES ====================

// ==================== FREEFORM TEMPLATE EXAMPLES ====================
const MY_ER_ROUNDS_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "ER Rounds",
  [TEMPLATE_FIELDS.CUSTOM]: `Patient name/signalment:
History:
Presenting complaint:
Completed diagnostics:
Pending Diagnostics:
Results:
Client expectations:
Plan:
…
Patient name/signalment:
History:
Presenting complaint:
Completed diagnostics:
Pending Diagnostics:
Results:
Client expectations:
Plan:`,
  preferences: `Fill in the rounds summary per patient discussed. Write in medical terminology, write in point form`,
  sampleTranscript: `Julius: 3 y/o MN DSH
Straining to urinate, vocalizing when using litter box. One episode of urinating blood outside the litter box. Completed radiographs, ultrasound, urinalysis no stones noted. Only red blood cells present on urinalysis. Plan IV fluids to flush bladder, Give Metacam SQ pending NSAID panel, Midazolam 0.2mg/kg IV q6hours for relaxation of sphincter

Scribbles: 7y/o MN DSH
Straining to defecate, markedly overweight, unable to groom himself. On lateral radiograph marked obstipation, cbc/chem10 all wnl. Owners cost concerned, have ok’d fluids- running those at 2x maintenance and enema’s (50/50 lube/water)  but no manual deobstipation

Dobby: 4 y/o MN Sheepdog mix 
Stick foreign body in L eye. Able to sedate and flush out. Marked corneal ulcer. Proceeding with temporary tarsorrhaphy under sedation and propofol later today. Bloodwork all normal.`,
};

const MY_STANDARD_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "Standard Summary",
  [TEMPLATE_FIELDS.CUSTOM]: `Summary:`,
  preferences: `Write a paragraph-long summary`,
  sampleTranscript: `Calling to discuss bloodwork with Otto's owner.Otto has been drinking and peeing more. Owner notes he is more hungry as well and has been losing weight. RBC, WBC platelets all normal. Blood glucose, kidney values, electrolytes and proteins are normal. ALT is 200 (RR 50-150), ALP is 2000 (RR 65- 200). Marked elevation in ALP could be due to Cushing's disease (hyperadrenocorticism). Recommend urinalysis to assess urine specific gravity, recommend low dose dexamethasone test in clinic to determine if Pituitary or Adrenal type. Can consider ultrasound as additional imaging but is more costly. Owner does not wish to pursue ultrasound.`,
};

const MY_RADIOLOGY_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "Radiology",
  [TEMPLATE_FIELDS.CUSTOM]: `What views were taken: (VD, DV, lateral, oblique or other views if mentioned, multiple views can be listed)
Quality: 
Patient preparation: (Sedated - list doses of drugs if mentioned, anesthetized, awake, other)

Radiographic Findings: (point form, use dashes)

Radiographic Assessment: (paragraph form)

Plan: (medications to go home, follow ups, etc noted here) 
`,
  preferences: `Use medical terminology where possible.`,
  sampleTranscript: `Okay, so we did a VD and a DV and a lateral X ray of the thorax while the patient, Dobby, was sedated. They are excellent quality radiographs. The X ray reveals a moderately enlarged cardiac silhouette with the loss of the cranial cardiac waist on the lateral view as well as the trachea being pushed dorsally. No evidence of perihilar edema. The assessment is stage C of heart failure with clinical changes as well as radiographic changes and a vertebral heart score of 12. Recommend start on Pimobendan and furosemide and recheck in a month.`,
};

const MY_CASE_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "Case Summary",
  [TEMPLATE_FIELDS.CUSTOM]: `Patient information:
History:
Summary of completed diagnostic findings:
Client expectations: 
Plan:`,
  preferences: `Write in medical terminology, write in point form`,
  sampleTranscript: `Winnie a 6yo intact female yorkie presented for vomiting and not eating for 3 days.
Radiographs revealed large closed pyometra, CBC/ Chem 10 revealed marked neutrophilia with left shift, mild evidence of dehydration with hemoconcentration
Client has consented to surgery, understands that patient is not out of the woods, recommend hospitalization overnight with continued fluid therapy, pain control. Consents to $6000 estimate maximum.`,
};

const MY_STRUCTURED_CALLBACK_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "Structured Callback",
  [TEMPLATE_FIELDS.CUSTOM]: `Discussion:
Results:
Plan:`,
  preferences: `Write in point form. Note if anything is declined by the owner. `,
  sampleTranscript: `Calling to discuss bloodwork with Otto's owner. Otto has been drinking and peeing more. Owner notes he is more hungry as well and has been losing weight. RBC, WBC platelets all normal. Blood glucose, kidney values, electrolytes and proteins are normal. ALT is 200 (RR 50-150), ALP is 2000 (RR 65- 200). Marked elevation in ALP could be due to Cushing's disease (hyperadrenocorticism). Recommend urinalysis to assess urine specific gravity, recommend low dose dexamethasone test in clinic to determine if Pituitary or Adrenal type. Can consider ultrasound as additional imaging but is more costly. Owner does not wish to pursue ultrasound.`,
};

const MY_STRUCTURED_SURGERY_OVH_FELINE_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "Structured Surgery - OVH Feline",
  [TEMPLATE_FIELDS.CUSTOM]: `Pre surg exam and bloodwork acceptable.
Preanesthetic Medications used:

Surgical notes:
The anesthetized animal was placed in dorsal recumbency and the area from just below the xiphoid to pubis was clipped and surgically prepared. The patient was surgically draped. A midline incision was made using a _____ blade through the skin and subcutaneous tissue.  The linea alba was located and gently elevated using forceps, and a stab incision was made into the linea alba using a ______ blade.  The incision into the linea was then extended cranially and caudal to the stab incision using metzenbaum scissors.

The uterus and ovaries were located and exteriorized. The suspensory ligaments on the right and left ovary were cut once fanned away from the pedicle. The ovarian pedicle was located and a window was made into the broad ligament right next to the vessels using a carmalt. A figure of 8 knot (pedicle tie) was used to provide hemostasis  to the pedicle. The knot was slid distally down to tighten it. The ovary was then freed from the pedicle by cutting above the knot and below the ovary using a scalpel blade. This was done to both the right and left pedicles. Both ovaries were ensured to be intact and hemostasis was confirmed.  Following the removal of the ovaries, the broad ligament was torn gently along the uterine body being careful of the uterine vessels.  The uterine body/cervix was clamped using carmalts. A millers knot, using ______ monocryl, was placed on the first crush when the first distal carmalt was removed.  A millers ligature using ______monocryl was used in the second crush from the second most proximal clamp.  The uterus was then removed by cutting above the ligature and below the clamp.  Once hemostasis was confirmed for all three sights, the abdomen was then closed.  The linea was closed using a simple continuous pattern of_____monocryl. The skin was closed using _______ monocryl suture in a intradermal pattern.

Recovery notes:
Recovery - E. tube pulled following return of swallowing reflex;  Recovery from anesthesia uneventful.

Medications to go home:
`,
  preferences: `Fill in the blanks`,
  sampleTranscript: `
Used 5micrograms/kg dexmedetomidine and 0.1mg/kg hydromorphone IM - excellent quality sedation: 
“I made an incision with a 10 blade and used 2-0 monocryl on the uterus. Then I used 3-0 monocryl on the linea and the skin.” 
Sending home: 5 days of oral Metacam (start tomorrow), 0.05mg/kg
  `,
};

const MY_SIMPLE_CLIENT_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "Simple Client Summary",
  [TEMPLATE_FIELDS.CUSTOM]: `Reason for Visit:
[Patient's name, what the visit was for. Include patient weight if stated in Objective section (lbs/kg). If a multi-pet visit include for each animal. Maximum 2 sentences.]

Findings:
Abnormal: [If abnormal findings are mentioned, summarize them in point form (these findings should be objective (ie. body system exam findings) but can be mentioned anywhere in the record. Pay attention to details about weight changes in this section, if any weight gain or weight loss is noted please list it as abnormal. If no abnormal findings are present, simply write: "No abnormal findings noted."]

Normal: [List all other body systems examined in a single concise sentence.]

Today, we:
[In point form, please list in past tense. Any new medications prescribed should be listed here with reason for prescribing and information about the prescription. Any tests or treatments completed during the appointment (even if the results are still pending) should be listed here. Do not include follow up recommendations or next steps.]

Recommendations:
[In point form, please list in present tense. Any next steps, new recommendation, changes discussed, costs to consider, follow-up information, appointments, procedures or tests that should be booked.]
`,
  preferences: `Write in clear, client-friendly language.`,
  sampleTranscript: `Max, 7y/o MC Labrador Retriever, presented for annual wellness exam and vaccinations. Weight is 32kg (70.4lbs), up 2kg from last year. No vomiting, diarrhea, coughing or sneezing. Eating and drinking normally. On Heartgard Plus monthly.

Physical exam: BAR, BCS 6/9. Mild tartar on molars, grade 1 dental disease. Heart and lungs normal. Mild crepitus noted in left stifle on range of motion testing. All other systems within normal limits.

Administered Rabies vaccine (RH SQ 3y), DHPP vaccine (LH SQ 3y).
Dispensed 6 months Simparica Trio for flea/tick/heartworm prevention.
Discussed weight management - recommend reducing food by 1/4 cup daily.
Recommend dental cleaning in next 6-12 months, estimated cost $800-1200.
Recommend Dasuquin for joint support, owner will consider.
Recheck in 1 year or sooner if limping worsens.`,
};

const MY_DISCHARGE_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE = {
  name: "Discharge Summary",
  [TEMPLATE_FIELDS.CUSTOM]: `Discharge Summary:
Include a summary of the appointment findings, focus on abnormal findings and any diagnostics mentioned. Include any differentials or diseases discussed and an in depth summary of these discussions. Include costs of any tests recommended but not performed. Include anything that was declined or deferred today (if not already mentioned). Include medications and specific reasons for sending home and how to administer. Include follow ups recommended.`,
  preferences: `Use the medical record to create a comprehensive discharge plan that is client facing.`,
  sampleTranscript: `Luna, 3y/o FS DSH, presented for vomiting and lethargy for 2 days. Owner reports 5-6 episodes of vomiting, decreased appetite, hiding behavior. No known toxin exposure. Indoor only cat.

Physical exam: Quiet but responsive. T 102.8F, HR 180, dehydrated 5-6%. Painful on abdominal palpation, no obvious foreign body felt. Rest of exam unremarkable.

Bloodwork: Mild elevation in WBC (19,000), everything else normal including kidney values and electrolytes.
Radiographs: Mild gas pattern in intestines, no obvious obstruction or foreign body visible.

Diagnosed with likely gastroenteritis, possibly from dietary indiscretion. Started on IV fluids in hospital for 4 hours. Administered Cerenia injection for nausea and Convenia injection for broad spectrum antibiotic coverage.

Discharged with:
- Cerenia 16mg tablets, give 1 tablet by mouth once daily for 3 days for nausea
- Bland diet (boiled chicken and rice) for next 3-5 days, then gradually transition back to regular food
- Monitor for continued vomiting, diarrhea, or worsening lethargy

Recheck in 3-5 days if not improving, or sooner if worsening. If foreign body obstruction is still suspected, ultrasound recommended (cost $400-600) or referral to specialist for endoscopy (cost $2000-3000). Owner declined ultrasound today, will monitor at home.`,
};

// ==================== END FREEFORM TEMPLATE EXAMPLES ====================

// ==================== TEMPLATE LIBRARY PRESET EXAMPLES ====================

const GeneralPracticeSoapTemplateExample = {
  name: "General Practice",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `[Remove any category below if no relevant information is mentioned]
Animal Advocate: (Who is here with the pet at the appointment- list a name if mentioned)
Lifestyle: (If noted, otherwise remove category)
Pet's signalment: (List Pet's name, gender, species if mentioned)
History: (Do not return any direct quotations. Keep under 75 words, do not report exam findings)
Diet: 
HW/ flea + tick preventative:
Current Medications/ supplements:
Any vaccine or medication reactions:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `[DO NOT REPORT ANY OWNER ANECDOTES in this section]
Temperature: not taken
Heart Rate: within normal limits
Weight: list in kg
Respiratory Rate: within normal limits
Capillary Refill Time: moist, <2 seconds
Mucous Membranes: pink
Hydration Status: normal hydration
Body Condition Score: 5/9
Pain Score: 0 (rate /4: 1 mild, 2-3 moderate, 4 severe- unable to touch/handle)
Fear Anxiety Stress Score: Green (Yellow if tension, nervous, anxious, Red if trying to bite)
Eyes: no clouding, no debris, sclera is clear
Ears: no debris or erythema
Nasal: normal
Oral/Dental: healthy teeth and gums
Cardiovascular: no murmur or arrhythmia, strong femoral pulses
Respiratory: lung sounds are clear, no crackles or wheezes
Neurological: normal mentation
Musculoskeletal: good range of motion, no gait deficits, normal muscling
Lymphatic: small, soft, pliable
Gastrointestinal: comfortable on palpation, no mass effect, no fluid wave
Reproductive/Urinary: normal conformation, no discharge
Integument: healthy skin and coat, no evidence of ectoparasites
Masses: no lumps or bumps noted`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Problem List: Remove category if none listed
Differentials: remove category if none listed
If vaccines are completed please include the statement: Apparently healthy, stable for vaccines`,
  [TEMPLATE_FIELDS.PLAN]: `Vaccines: if given denote location: Rabies RH SQ 3y, DHPP 3y LH SQ, Lepto 1y LH SQ, Kennel Cough 1y given by mouth
Treatments: Injections/ medications applied or given orally during the appointment. Do not repeat vaccinations here.
Diagnostics Completed:
Prescriptions:
Client communication: (summarize in point form any topics reviewed with the owner, Keep under 75 words, do not repeat information from previous sections)
Follow up: Remove category if none specified`,
  preferences: `Use medical terminology.
Note if anything is declined.
Include costs if mentioned.
Write history and client communication in point form (use dashes).
Bold abnormals in objective section`,
};

const GeneralIllnessTemplateSoapTemplateExample = {
  name: "General Illness Template",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `Pet's Name: 
Owner: (Clients First Name if mentioned)
Reason for Exam: (One sentence)

Last Exam: 
HWT: 
FeLV/FIV Hx: (Remove category if not a feline)  
Fecal Hx:
Vacc: (Is vaccine status current)
Current Medications:
Parasite Prevention Products: (List flea tick and/or heartworm medication here)
Diet:
Lifestyle:
Medical History & Symptoms: (Only past history. DO NOT LIST EXAM FINDINGS FROM OBJECTIVE CATEGORY HERE. Use point form (dashes))`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `Pain Score:  0 /4 (rate /4: 1 mild, 2-3 moderate, 4 severe- unable to touch/handle) 
Fear Anxiety Stress Score: Green (Yellow if tension, nervous, anxious, Red if trying to bite) 
Temp: - °C
Heart Rate: 
Resp Rate::
Weight: list in kg
BCS: 5/9 (classification here: 5/9 ideal, 9/9 obese) 

Exam Findings:
1. General Appearance: [ ] Normal [ ] Abnormal [ ] NE
2. Eyes: [ ] Normal [ ] Abnormal [ ] NE
3. Ears: [ ] Normal [ ] Abnormal [ ] NE
4. Mouth, Teeth, & Gums: [ ] Normal [ ] Abnormal [ ] NE
5. Respiratory System: [ ] Normal [ ] Abnormal [ ] NE
6. Cardiovascular System: [ ] Normal [ ] Abnormal [ ] NE
7. Abdomen/Digestive: [ ] Normal [ ] Abnormal [ ] NE
8. Urogenital System: [ ] Normal [ ] Abnormal [ ] NE
9. Musculoskeletal System: [ ] Normal [ ] Abnormal [ ] NE 
10. Lymph Nodes/Endocrine: [ ] Normal [ ] Abnormal [ ] NE
11. Skin & Coat: [ ] Normal [ ] Abnormal [ ] NE
12. Nervous System: [ ] Normal [ ] Abnormal [ ] NE

Abnormal Descriptions: 
(List ONLY the number of the category -not the subheading- and the associated abnormal findings here)`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Problem list: Remove category if none listed
Differentials: Remove category if none listed`,
  [TEMPLATE_FIELDS.PLAN]: `Treatments: Injections/ medications applied or given orally during the appointment/ procedures. (If procedures list sedation protocol if mentioned)
Diagnostics Completed:
Prescriptions: 
Instructions/Recommendations: (summarize in point form any topics reviewed with the owner, Keep under 200 words, do not repeat information from previous sections)
Next Exam: Remove category if none specified`,
  preferences: `Use veterinary medical terminology where possible.  
Note if anything is declined. 
Include costs if mentioned. 
Write history and Instructions/Recommendations in point form (use dashes).`,
};

const ReviewingResultsFreeformTemplateExample = {
  name: "Reviewing Results",
  [TEMPLATE_FIELDS.CUSTOM]: `Review of Results
Dr. *** reviewed results with (owner's name if known) (in person / via phone).
Results:
Discussion:
Plan:`,
  preferences: `Write in point form under subheadings (only use dashes). Include detailed discussion points.
Note if anything is declined by the owner. Note any costs discussed.
Refer to the Veterinarian as Dr.***
Refer to the owner by their name (if known).`,
};

const CanineDentalFreeformTemplateExample = {
  name: "Canine Dental",
  [TEMPLATE_FIELDS.CUSTOM]: `**Canine Dental Findings:**

**Upper Right Quadrant 100s** (this is the right maxilla)
101: (one of the upper right incisors; Right maxillary first incisor)
102: (one of the upper right incisors; Right maxillary second incisor)
103: (one of the upper right incisors; Right maxillary third incisor)
104: (upper right canine; Right maxillary canine)
105: (one of the upper right premolars; Right maxillary first premolar)
106: (one of the upper right premolars; Right maxillary second premolar)
107: (one of the upper right premolars; Right maxillary third premolar)
108: (one of the upper right premolars; Right maxillary fourth premolar; upper right carnassial)
109: (one of the upper right molars; Right maxillary first molar)
110: (one of the upper right molars; Right maxilly second molar)

**Upper Left Quadrant 200s** (this is the left maxilla):
201: (one of the upper left incisors; left maxillary first incisor)
202: (one of the upper left incisors; left maxillary second incisor)
203: (one of the upper left incisors; left maxillary third incisor)
204: (upper left canine; left maxillary canine)
205: (one of the upper left premolars; left maxillary first premolar)
206: (one of the upper left premolars; left maxillary second premolar)
207:(one of the upper left premolars; left maxillary third premolar)
208: (one of the upper left premolars; left maxillary fourth premolar; upper left carnassial)
209: (one of the upper left molars; left maxillary first molar)
210: (one of the upper left molars; left maxillary second molar)

**Lower Left Quadrant 300s** (this is the left mandible):
301: (one of the lower left incisors; left mandibular first incisor)
302: (one of the lower left incisors; left mandibular second incisor)
303: (one of the lower left incisors; left mandibular second incisor)
304: (lower left canine; left mandibular canine)
305: (one of the lower left premolars; left mandibular first premolar)
306: (one of the lower left premolars; left mandibular second premolar)
307: (one of the lower left premolars; left mandibular third premolar)
308: (one of the lower left premolars; left mandibular fourth premolar)
309: (one of the lower left molars; left mandibular first molar; left lower carnassial)
310: (one of the lower left molars; left mandibular second molar)
311: (one of the lower left molars; left mandibular third molar)

**Lower Right Quadrant 400s** (this is the right mandible):
401: (one of the lower right incisors; right mandibular first incisor)
402: (one of the lower right incisors; right mandibular second incisor)
403: (one of the lower right incisors; right mandibular third incisor)
404: (lower right canine; right mandibular canine)
405: (one of the lower right premolars; right mandibular first premolar)
406: (one of the lower right premolars; right mandibular second premolar)
407: (one of the lower right premolars; right mandibular third premolar)
408: (one of the lower right premolars; right mandibular fourth premolar)
409: (one of the lower right molars; right mandibular first molar; right lower carnassial)
410: (one of the lower right molars; right mandibular second molar)
411: (one of the lower right molars; right mandibular third molar)

**Procedure Summary**:
- Including the admit, pre-surgical blood work, medications that the pet is currently on, Premedications, Anesthetic comments, procedure performed, Grade or stage of disease or any general comments about disease level

**Radiograph Findings**:

**Treatments**:
- Including local blocks, persistent retained deciduous teeth, any extractions; how they were performed, and sutures used during procedure, if mentioned.

**Plan going home**:
- Recommendations and any home care plans or medication to take home, if mentioned. (Use dashes)`,
  preferences: `Use Veterinary dental terminology. Use the numerical system or the terminology provided to fill in the findings as stated. If a tooth is stated as 'normal' or 'stable', return "Normal" as the value for that tooth. If there is no information for a tooth, return 'Not mentioned'. Remove descriptors and instructions from final output.`,
};

const FelineDentalFreeformTemplateExample = {
  name: "Feline Dental",
  [TEMPLATE_FIELDS.CUSTOM]: `**Upper Right Quadrant 100s** (right maxilla)
 
101: (one of the upper right incisors)
102: (one of the upper right incisors)
103: (one of the upper right incisors)
104: (upper right canine)
106: (one of the upper right premolars)
107: (one of the upper right premolars)
108: (one of the upper right premolars)
109: (upper right molar)

**Upper Left Quadrant 200s** (left maxilla)
      
201: (one of the upper left incisors)
202: (one of the upper left incisors)
203: (one of the upper left incisors)
204: (upper left canine)
206: (one of the upper left premolars)
207: (one of the upper left premolars)
208: (one of the upper left premolars)
209: (upper left molar)

**Lower Left Quadrant 300s** (left mandible)

301: (one of the lower left incisors)
302: (one of the lower left incisors)
303: (one of the lower left incisors)
304: (lower left canine)
307: (one of the lower left premolars)
308: (one of the lower left premolars)
309: (lower left molar)

**Lower Right Quadrant 400s** (right mandible)
   
401: (one of the lower right incisors)
402: (one of the lower right incisors)
403: (one of the lower right incisors)
404: (lower right canine)
407: (one of the lower right premolars)
408: (one of the lower right premolars)
409: (lower right molar)

**Procedure Summary**:
- Including the admit, pre-surgical blood work, medications that the pet is currently on, Premedications, Anesthetic comments, procedure performed, Grade or stage of disease or any general comments about disease level

**Radiograph Findings**:

**Treatments**:
- Including local blocks, persistent retained deciduous teeth, any extractions; how they were performed, and sutures used during procedure, if mentioned.

**Plan going home**: 
- Recommendations and any home care plans or medication to take home, if mentioned. (Use dashes)`,
  preferences: `Use Veterinary dental terminology. Use the numerical system or the terminology provided to fill in the findings as stated. If a tooth is stated as 'normal' or 'stable', return "Normal" as the value for that tooth. If there is no information for a tooth, return 'Not mentioned'. Remove descriptors and instructions from final output.`,
};

const StaffMeetingFreeformTemplateExample = {
  name: "Staff Meeting",
  [TEMPLATE_FIELDS.CUSTOM]: `Date:
Meeting Lead:

Wins & Shout-outs: Include names and include win/shoutout, use point form 

Team Updates: include anything to do with scheduling/staff, policy or workflow updates and upcoming events/reminders. Use point form

Discussion Items: Include 1-2 items

Topic: What's changing/being discussed
Outcome/decision

Action Items: Include what needs to be done, by who and by when. Use point form

Blockers: include what is making jobs harder and support needed. Use point form

Key Takeaways: Use point form

Next Meeting Date:`,
};

const HospitalManagerAndEmployee1On1TemplateExample = {
  name: "Hospital Manager and Employee 1-on-1",
  [TEMPLATE_FIELDS.CUSTOM]: `Employee Name and Position:
First and last name

Reason for meeting:
Provide one sentence

Summary of meeting
Use point form (dashes)

Next Steps:
Use point form (dashes)

Next meeting:
Use date format mm/dd/yyyy`,
};

const ErTemplateSoapTemplateExample = {
  name: "ER Template",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `[Write a history in layman's terms using these subheadings]
Presenting Complaint: [1-5 words]
History: Start with the signalment and then write a concise history
Medications:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `[Leave out any anecdotal information from the customer]
General Appearance: BAR
Fear Anxiety Stress Score: Green
Weight: - kg
Temperature: Not taken
HR: WNL
RR: WNL
BCS: 5/9
Eyes: clear
Ears: No debris or erythema
Oral Cavity: healthy teeth and gums
Cardiovascular: Regular rhythm; no murmur detected, strong pulses.
Respiratory: Lungs auscultated clear bilaterally.
Abdomen: Abdomen palpates normally
Musculoskeletal: Normal gait
Integument: healthy skin and coat
Lymph nodes: soft, small, pliable
Urogenital: normal on exam
Neurologic: Within normal limits
Rectal: Not examined`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `List out all active problems that the patient is experiencing with the associated differential diagnosis in this format: "problem: r/o differential diagnosis vs differential diagnosis vs..."`,
  [TEMPLATE_FIELDS.PLAN]: `Recommendations:
Write initial recommendations in a paragraph form for detailed initial clinical suspicions and treatment plan utilizing the words recommended, offered, and discussed. Indicate any declined recommendations.
Progress Notes:
Write updates following the recommendations, capturing any clinical progress, treatments, diagnostics, or customer communications in chronological order. List out any medication doses with the dose (mg/kg), volume (ml), route (IV, IM, SQ) and location given. List any abnormal labwork or diagnostic findings discussed.
-------------------------------------------------------------------------------------------------
DISCHARGE:
Create concise and customer-friendly discharge instructions under the following subheadings:
WHAT TO DO:
Write any details the customer needs to succeed in taking care of their pet at home, including:
- Medications: [include the dose, concentration, volume, quantity]
- At home care details
- Dietary instructions
MONITORING:
Include any reasons related to the patient's condition that would warrant the customer calling or
returning to the ER, such as increased lethargy, vomiting, diarrhea, or not eating.
NEXT STEPS:
Write out any follow-up instructions the customer may need to follow after leaving the clinic,
including the date, location, testing, or procedure details.
Include this line at the very end:
Thank you for trusting us to care for your cherished family member. Please return to your family
veterinarian for your next regularly scheduled appointment. In the meantime, do not hesitate to
return or call with any concerns or questions.
Best,
[Include my name] and the patient care team at Scribenote Veterinary Clinic
-------------------------------------------------------------------------------------------------
Include a friendly follow-up email with personable details from the visit to check in on the patient
and remind the owners of next steps.`,
};

const CprFreeformTemplateExample = {
  name: "CPR",
  [TEMPLATE_FIELDS.CUSTOM]: `CPR Template

Crash Cart Checked: [Yes / No] (Indicates pre-event preparedness) [Leave off if not discussed]
Team Roles Assigned: [Leader, Compressor, Ventilator, Scribe, Drug Runner] [Leave off if not discussed]

Time of Arrest (CPA): [HH:MM:SS]
   CPR Initiated: [HH:MM:SS]
   Confirmation of Arrest:
   Check for: [Unresponsive, Apnea]

Airway Management:
   Airway Status: [Patent / Obstructed]
   Method: [Intubation / Mouth-to-snout / Other] 
   ET Tube Size: [Size]

Confirmation: [Auscultation, Chest movement, Capnometry]

Chest Compressions:
   Cycle Duration: [2 Minutes]
   Compressor Changes: [Every 2 minutes]
   Rate: [100-120 compressions/minute]
   Technique: [Lateral / Dorsal / Circumferential]

Ventilation:
   Method: [Ambu bag, Anesthetic machine]
   Rate: [10 breaths/minute (1 breath every 6 seconds)]
   Tidal Volume: [Visible chest rise]

Monitoring Initiated:
[ ] ECG
[ ] EtCO2

Vascular Access:
Route: [IV / IO / IT]
Location: [e.g., Cephalic, Jugular]

First ECG Rhythm: [Asystole, Pulseless Electrical Activity (PEA), Ventricular Fibrillation (VFib)]

Medication Administration Log:

Drug: [e.g., Epinephrine, Atropine]
Dose: [Calculated dose based on weight]
Route: [IV / IO / IT]
Time: [HH:MM:SS]

Electrical Defibrillation (if VFib/Pulseless VTach): [Leave off if not discussed]
Number of Shocks: [e.g., 1st shock, 2nd shock]
Energy: [e.g., 4 J/kg]
Time: [HH:MM:SS]

EtCO2 Values:
Initial: [Value] mmHg
Goal: [>15-20 mmHg]
Changes: [e.g., EtCO2 dropped to 10 mmHg at 15:45]

Post-Cardiac Arrest Care (PCAC): [Leave off if not discussed]
Return of Spontaneous Circulation (ROSC):
Time of ROSC: [HH:MM:SS]

If ROSC achieved: [Leave off if not discussed]
Algorithm Initiated: [Post-Cardiac Arrest Care Algorithm: oxygen therapy, hemodynamic support, continuous monitoring]
Initial Actions: [e.g., Oxygen therapy, fluid therapy, continued monitoring]

If ROSC not achieved: [Leave off if not discussed]
Reason for Termination: [e.g., >20 minutes of CPR, client request, clinical decision]
Time of Death: [HH:MM:SS]

Client Communication: [Time of discussion, key points]

Summary & Debriefing: [Leave off section if not discussed]
Key Events: [e.g., Patient arrested during anesthesia, first rhythm was PEA, two doses of epinephrine given]
What went well: [e.g., Rapid intubation, consistent compressor changes]
Areas for Improvement: [e.g., Difficulty with vascular access, drug calculations were slow]
Final Thoughts/Notes: [Any other details relevant to the event]`,
};

const InpatientFreeformTemplateExample = {
  name: "Inpatient",
  [TEMPLATE_FIELDS.CUSTOM]: `[DATE], doctor name, shift time (am or pm)
Remove any sections below that are not applicable or not discussed during this shift.

Rounds:
Reason for hospitalization:
Summary of events since admission (diagnoses, key findings, complications, if any):
Clinical status at start of shift (subjective + objective highlights):
Response to treatments to date:
Active problems being monitored this shift:
Plan for this shift:
Owner update provided during rounds (what was discussed and decisions made):

Customer communications:
[time, if stated, otherwise remove and keep in chronological order]  – method (phone/text/in person): summary of discussion, updates provided, questions asked, decisions made, and consent obtained if applicable
[time, if stated, otherwise remove and keep in chronological order] – method:

Progress notes:

[time, if stated, otherwise remove and keep in chronological order] – patient status update (vitals, mentation, appetite, pain, etc.):
[time, if stated, otherwise remove and keep in chronological order] – treatments administered (drug, dose, route):
[time, if stated, otherwise remove and keep in chronological order] – diagnostics performed or resulted (labs, imaging, monitoring):
[time, if stated, otherwise remove and keep in chronological order] – response to treatment/reassessment:

Procedure notes:
Remove this section if no procedures were performed during this shift.
[time, if stated, otherwise remove and keep in chronological order] – procedure performed:

 indication:
 anesthesia/sedation (if applicable):
 description of procedure:
 complications (if any):
 patient tolerance and recovery:

Plan:

Ongoing treatments and monitoring:
Diagnostics pending or planned:
Anticipated changes for next shift:
Discharge planning considerations (if applicable):
Key points discussed and agreed upon with owner:

Finances:
Remove this section if there were no financial updates or discussions.

Current invoice amount:
Updated estimate amount (if changed):
Reason for change in estimate:
Financial discussion with owner and approval status:`,
  preferences: `Write  in point form (use dots). Concise. Do not use markdown. Only use plain text`,
};

const CanineNeuterFreeformTemplateExample = {
  name: "Canine Neuter",
  [TEMPLATE_FIELDS.CUSTOM]: `Pre-surgical exam and bloodwork acceptable

Pre-surgical Drug Protocol: [Write the drugs using the Name, concentration, dose, volume and route]

The anesthetized patient was placed in dorsal recumbency. The patient was surgically
clipped from base of penis to scrotum, prepared and draped. The right testicle is manually
pushed forward from the scrotum to the midline area towards the base of the penis. A
midline incision was made over the testicle through the skin and subcutaneous tissues. The
incision was made through the testicular tunic and the testicle was exteriorized. The
testicular cord is clamped with two curved carmalts. A millers knot using ____ monocryl was
placed on the most distal crush when the first carmalt was removed. A miller's knot was
placed on the most proximal carmalt crush. The testicle was then removed by cutting
between the miller's knot (most distal ligature) and the testicle. One cruciate suture was
placed through the right testicular tunic to minimize bleeding. The same was done with the
left testicle and tunic. Both cords were assessed for hemostasis before allowing them to
retract back into the scrotum. The subcutaneous tissues were closed using ____
monocryl in a simple continuous pattern. And the skin was closed using ____ monocryl in
a subcuticular pattern.

Recovery: E. tube pulled following return of swallowing reflex; Recovery from anesthesia
uneventful. Scrotum iced during recovery

Medications to go home:
Instructions for at home care:`,
  preferences: `Fill in the blanks where appropriate.`,
};

const CanineSpayFreeformTemplateExample = {
  name: "Canine Spay",
  [TEMPLATE_FIELDS.CUSTOM]: `Elective ovariohysterectomy
[DATE]
Attending Veterinarian:
Assistants:

Patient Name:
Species/Breed:
Age:
Weight:

Pre-Operative Assessment
Physical exam findings:
Pre-op diagnostics (if any):
ASA status:
Fasting status:
Pre-operative concerns/complications:

Anesthesia & Analgesia
Premedication:
Induction agent:
Maintenance:
Local blocks (if applicable):
Intraoperative analgesia:
IV fluids:
Monitoring parameters (TPR, BP, SpO₂, ECG):

Surgical Site Preparation:
Ventral abdomen clipped and aseptically prepped in standard fashion.

Procedure Description
Patient positioned in dorsal recumbency. A ventral midline incision was made through skin and subcutaneous tissue. The linea alba was identified and incised. The ovaries were located sequentially. Each ovarian pedicle was isolated, ligated using appropriate suture material, and transected. The uterine body was identified, double-ligated, and transected cranial to the cervix. The abdomen was explored for hemorrhage; none noted. The abdominal wall was closed routinely in multiple layers.

Closure
Linea alba:
Subcutaneous tissue:
Skin:
Suture pattern/material:

Intraoperative Findings
Normal/abnormal reproductive tract:
Complications: None noted / Describe if present

Recovery
Patient recovered uneventfully from anesthesia. Extubated when swallowing reflex returned. Vital parameters stable during recovery.

Post-Operative Care
Pain management plan:
Antibiotics (if indicated):
E-collar: Yes / No
Activity restriction:
Incision care instructions provided to owner

Discharge Instructions
Recheck recommended:
Suture removal date:
Owner advised to monitor for swelling, discharge, lethargy, or inappetence

Best,
Dr. 
Scribenote Veterinary Clinic
4 Main Street, Smithtown, ON
(401) 234-9879`,
  preferences: `Use veterinary medical terminology where possible.`,
};

const FelineNeuterFreeformTemplateExample = {
  name: "Feline Neuter",
  [TEMPLATE_FIELDS.CUSTOM]: `Bloodwork reviewed and previously relayed to owner. Acceptable for surgery
Pre-surgical medications and anesthetic comments/notes:

The patient was clipped and prepped and placed in lateral recumbency. The testicles were blocked with lidocaine_____. A small 1 cm incision was made in the most ventrally located testicle through the tunic. The spermatic cord and vasculature was ligated via autoligation (a figure of 8 knot) using a small pediatric mosquito forcep. The knot was ensured to be tight and hemostasis was confirmed before placing within the tunic. The same procedure was completed for the 2nd (more dorsally located) testicle. 

Medications to go home:`,
  preferences: `Fill in the blanks. Use veterinary medical terminology where possible.`,
};

const FelineSpayFreeformTemplateExample = {
  name: "Feline Spay",
  [TEMPLATE_FIELDS.CUSTOM]: `Pre surg exam and bloodwork acceptable.
Preanesthetic Medications used:

Surgical notes:
The anesthetized animal was placed in dorsal recumbency and the area from just below the xiphoid to pubis was clipped and surgically prepared. The patient was surgically draped. A midline incision was made using a _____ blade through the skin and subcutaneous tissue.  The linea alba was located and gently elevated using forceps, and a stab incision was made into the linea alba using a ______ blade.  The incision into the linea was then extended cranially and caudal to the stab incision using metzenbaum scissors.

The uterus and ovaries were located and exteriorized. The suspensory ligaments on the right and left ovary were cut once fanned away from the pedicle. The ovarian pedicle was located and a window was made into the broad ligament right next to the vessels using a carmalt. A figure of 8 knot (pedicle tie) was used to provide hemostasis  to the pedicle. The knot was slid distally down to tighten it. The ovary was then freed from the pedicle by cutting above the knot and below the ovary using a scalpel blade. This was done to both the right and left pedicles. Both ovaries were ensured to be intact and hemostasis was confirmed.  Following the removal of the ovaries, the broad ligament was torn gently along the uterine body being careful of the uterine vessels.  The uterine body/cervix was clamped using carmalts. A millers knot, using ______ monocryl, was placed on the first crush when the first distal carmalt was removed.  A millers ligature using ______monocryl was used in the second crush from the second most proximal clamp.  The uterus was then removed by cutting above the ligature and below the clamp.  Once hemostasis was confirmed for all three sights, the abdomen was then closed.  The linea was closed using a simple continuous pattern of_____monocryl. The skin was closed using _______ monocryl suture in a intradermal pattern.

Recovery notes:
Recovery - E. tube pulled following return of swallowing reflex;  Recovery from anesthesia uneventful.

Medications to go home:`,
  preferences: `Fill in the blanks`,
};

const DetailedAbdominalUltrasoundFreeformTemplateExample = {
  name: "Detailed Abdominal Ultrasound",
  [TEMPLATE_FIELDS.CUSTOM]: `Ultrasound (AUS) Detailed

Indication for Study:
(Reason for ultrasound, brief clinical context)

Technique:
Abdominal ultrasound performed with appropriate transducer(s). Images obtained in multiple planes. Patient positioning and study quality noted as needed.

Findings

Liver:
Size, margins, and echotexture:
Presence or absence of focal or diffuse lesions, nodules, or masses:
Hepatic vasculature (if evaluated):

Gallbladder & Biliary System:
Gallbladder wall:
Contents (e.g. anechoic bile, sludge, choleliths):
Common bile duct diameter and appearance:

Pancreas:
Size and margins:
Echogenicity relative to mesenteric fat:
Peripancreatic fat and surrounding structures:

Spleen:
Size and margins:
Echotexture:
Focal lesions or masses:

Kidneys:
Left Kidney:
Size and shape:
Corticomedullary definition:
Renal pelvis:
Length: ____ mm

Right Kidney:
Size and shape:
Corticomedullary definition:
Renal pelvis:
Length: ____ mm

Adrenal Glands:
Left Adrenal:
Shape and echogenicity:
Thickness: ____ mm

Right Adrenal:
Shape and echogenicity:
Thickness: ____ mm

Urinary Bladder:
Wall thickness and layering:
Luminal contents (e.g. anechoic urine, sediment, uroliths):

Prostate (male patients only):
Size and symmetry:
Echotexture and margins:

Uterus/Ovaries (female patients if applicable):
Uterine horns and body:
Ovarian appearance:

Stomach:
Wall thickness and layer stratification:
Inter-rugal thickness: ____ mm
Contents (e.g. gas, fluid, ingesta):
Pylorus:

Small Intestine:
Wall thickness and layer stratification:

Duodenum: ____ mm
Jejunum: ____ mm
Ileum: ____ mm

Motility (if assessed):

Colon:
Wall thickness and contents:

Lymph Nodes:
Location(s) assessed:
Size, shape, and echogenicity:

Major Vessels:
Visible portions of the aorta, caudal vena cava, and portal vein:

Peritoneum:
Presence or absence of free fluid, fat changes, or masses:

Conclusions

Ultrasonographic Interpretation:
(Summarize key findings using medical terminology)

Differential Diagnoses:
(List differentials based on imaging findings and clinical context)

Recommendations:
(Additional diagnostics, monitoring, repeat imaging, or treatment considerations)

Write a friendly, professional follow-up email to the client after the results have been discussed, summarizing the key ultrasound findings in clear language, outlining any recommended treatments or next steps, and reinforcing follow-up instructions so that the client feels informed, confident, and supported.`,
  preferences: `Use medical terminology where possible.`,
};

const CardiologySoapTemplateExample = {
  name: "Cardiology",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `Patient Name:
Presenting complaint/Reason for Referral:
(Do not return direct quotations)

Patient History: (summarize in 50-75 words or less, use point form (dashes))

Symptoms: 
Is your pet coughing? (Yes/ No)
Is your pet having any increased respiratory effort or experiencing laboured breathing?
Does your pet's breathing appear abnormally rapid?
History of fainting/seizing episodes?:
What is your pet's exercise capacity?
E/D/BM/U:
V/D:

Other Pets at Home:
Diet Information:
Relevant Previous Medical History/Vaccine History:
Recent Travel History:
Heartworm Prevention:
Current Medications and Refills Needed:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `(Do not return any owner anecdotes in this section)

BW (kg):
Temp:
HR:
RR:
BP:
SpO2:
Mentation: Bright, alert and responsive.
Mucous membranes: Pink, CRT <2s.
Jugular veins: No distension.
Heart: No murmur. No arrhythmia.
Femoral pulses: Strong, synchronous femoral pulses.
Respiratory: Normal bronchovesicular lung sounds, no crackles, no wheezes.
General Assessment: BAR, euhydrated. BCS 4/9, MCS 4/4. mm pink, CRT <2s.
EENT: Oral cavity exam unremarkable. OU and AU grossly normal.
Abdomen: Soft, non painful, pliable. No overt mass. No fluid wave.
Urogenital: Grossly normal.
Rectal Exam: Not examined.
Musculoskeletal: Ambulatory, no lameness. Full orthopedic and neurological exam not performed.
Neurological: Alert and appropriate. No obvious cranial nerve or postural deficits were appreciated on cursory assessment.
Integument: Grossly normal.
PLN: No lymphadenopathy.

ECG findings/ notes: [Remove if none mentioned]
(using medical terminology, summarize in point form (use dashes) findings noted during the ECG monitoring, include wave forms if mentioned)

Echo findings:[Remove if none mentioned]
(using medical terminology, summarize in point form (use dashes) findings noted during the cardiac ultrasound/echo, include measurements)

Other tests:[Remove if none mentioned]
(using medical terminology, summarize in point form (use dashes) findings not noted in the above categories. Remove if empty.)`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `List out all active problems that the patient is experiencing with the associated differential diagnosis in this format: "problem: r/o differential diagnosis vs differential diagnosis vs..."`,
  [TEMPLATE_FIELDS.PLAN]: `Medications: [Clearly write each medication as either a new prescription or a refill, and explicitly note any changes made during today’s visit so the client can easily identify and understand them after returning home.]

Discussion: (50-100 word summary in point form (use dashes) of client communication)

Follow-up appointments/ tests required: Remove if none mentioned

(Always include the following at the bottom of the plan:)
Kind Regards,
DVM, DACVIM (Cardiology)
Cardiology Service
email@cardiovethospital.com

Generate a friendly, professional, and personalized follow-up email to the client summarizing any treatment plan changes, clearly outlining medications, and providing detailed follow-up instructions including recommended tests and their locations.`,
  preferences: `[Leave out any categories that are not mentioned or discussed]

Do not use mardown. Only use plain text`,
};

const LargeAnimalEquineGeneralSoapTemplateExample = {
  name: "Large Animal / Equine General",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `(History provided by owner/handler; do not use direct quotations)
Animal ID / Name:
Species / Breed:
Age:
Sex / Reproductive Status:
Use / Role (e.g. performance, breeding, production, companion):
Herd / Farm Name (if applicable):

Presenting Complaint:

History:
(Summarize timeline, duration, progression, and any treatments attempted)

Herd History [Remove if not applicable]:
Recent illnesses, new additions, morbidity/mortality, vaccination or deworming status

Management & Environment: [Remove if not applicable]
Housing, turnout, bedding, footing, weather exposure

Diet & Nutrition:
Forage type, concentrate, supplements, water source

Medications & Preventatives:
(Current medications, vaccines, deworming, reproductive protocols)

Travel / Movement History: [Remove if not applicable]

Behavior, Appetite & Production:
Feed intake, water intake, weight change, milk production, performance changes

Elimination:
Urination, defecation, manure consistency

Reproductive History: [Remove if not applicable]
Breeding status, pregnancy status, foaling/calving history`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `(No owner observations in this section)
Temperature:
Heart Rate:
Respiratory Rate:
Weight or Estimated Weight:
Body Condition Score:
General Appearance & Attitude:
Bright, quiet, depressed, painful, recumbent
Hydration Status:
Mucous Membranes & CRT:
Cardiovascular:
Heart sounds, rhythm, peripheral pulses
Respiratory:
Effort, lung sounds, nasal discharge
Gastrointestinal:
Gut sounds, abdominal contour, manure character
Musculoskeletal / Lameness:
Gait, stance, swelling, heat, pain response
Integument:
Skin, coat, wounds, lesions
Neurologic:
Mentation, cranial nerves, coordination
Reproductive / Mammary [Remove if not applicable]:

Diagnostics
[Use medical terminology; remove if none performed]
Laboratory Tests:
Bloodwork, fecal analysis, milk testing, cultures
Imaging:
Ultrasound, radiographs, endoscopy
Other Tests / Procedures:`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Problem List:
Primary Diagnosis: [Remove if not applicable]
Differential Diagnoses:
Prognosis: [Remove if not applicable]
Good / Fair / Guarded / Poor`,
  [TEMPLATE_FIELDS.PLAN]: `Treatment Plan:
Medications, dosages, routes, duration

Medications:
Clearly indicate new prescriptions versus refills and highlight any changes

Management & Husbandry Recommendations: [Remove if not applicable]
Housing, exercise, biosecurity, isolation if indicated

Nutrition Recommendations: [Remove if not applicable or discussed]

Follow-Up & Monitoring:
Rechecks, response parameters, when to call

Client Communication:
Summary of findings, risks, expectations, and care instructions

Kind Regards,
DVM
Large Animal / Equine Medicine
Practice Name
email@practice.com

Generate a friendly, professional, and personalized follow-up email summarizing treatment changes, medications, and follow-up instructions, including test details and locations.`,
};

const LargeAnimalOntarioMilkAndMeatWithdrawalTimesTemplateExample = {
  name: "Large Animal (Ontario Milk and Meat Withdrawal Times)",
  [TEMPLATE_FIELDS.CUSTOM]: `Presenting complaint: 
Days in Milk: 
Lactation: 
Diet: 

Temperature: 
Respiratory: WNL
Abdominal Auscultation: (include information of Right and Left side) WNL
Thoracic Auscultation: Normal rate and rhythm 
Rectal Palpation: Normal manure quality, normal tone
Udder: No abnormal milk
Reproductive: (note if open or pregnant) or wnl 

Ketones: 
BCS: (out of 5)

Lameness: Normal gait assessment

Differentials: 

Plan: (ensure to differentiate if noted whether the farmer has the medication and will continue to dispense or whether the veterinarian is leaving the medication for the farmer to initiate treatment)

Instructions to farmer:`,
  preferences: `Use large animal medical terminology where possible.
Always add "days milk" and "days meat" withdrawal to the numbers in the legend and return in the format (# days milk withdrawal; # days meat withdrawal)  
when the specific medication or treatment is mentioned within the appointment 
*Cal-Boost Calcium Bolus Promo: 0; 0
*Dehorning Solution 100ml: 2; 3		
Bioestrovet - 100ml: 0; 1
Bioestrovet - 50ml:  0; 1
Boss Pour On - 5L JUG: 0; 1
Boss Pour On 3.78L:	0; 1
Boss Pour On 5L:  0; 1
Boss Pour On 5L: 0; 1
Boss Pour-On - 900ml: 0; 1
Bovilis Coronarvirus - 10ds:	0;	21
Bovilis Coronavirus - 25 x 1 ds: 0; 21
Bovilis Guardian - 10ds: 0; 21
Bovilis Guardian - 10ds: 0; 21
Bovilis Guardian - 50ds: 0; 21
Bovilis Guardian - 50ds: 0; 21
Bovilis Guardian - BOTTLE/10ds: 0; 21
Bovilis Guardian - BOTTLE/50ds: 0; 21
Bovilis Nasalgen - DOSE/10:	0; 21
Bovilis Nasalgen 3-PMH - 10d: 0; 21
Bovilis Nasalgen 3-PMH - 10d: 0; 21
Bovilis Nasalgen 3-PMH - 10d (Case of 24): 0; 21
Bovilis Nasalgen 3-PMH - 25x1ds: 0; 21
Bovilis Nasalgen 3-PMH - 25x1ds: 0; 21
Bovilis Nasalgen 3-PMH - 50ds: 0; 21
Bovilis Nasalgen 3-PMH - TRAY/25DS: 0; 21
Bovilis Nasalgen 50ds: 0; 21
Bovilis Nasalgen DOSE/50: 0; 21
Bovi-shield Gold 5 10d - OLP: 0; 21
Bovi-shield Gold 5 50d - OLP: 0; 21
Bovi-shield Gold 5 5d - OLP: 0; 21
Bovi-shield Gold FP5 - BOX/50ds: 0; 21
Bovi-shield Gold FP5 - DOSE/10: 0; 21
Bovi-shield Gold FP5 - DOSE/5: 0; 21
Bovi-shield Gold FP5 10d - OLP: 0; 21
Bovi-shield Gold FP5 10d - OLP: 0; 21
Bovi-shield Gold FP5 50d- OLP: 0; 21
Bovi-shield Gold FP5 50d- OLP: 0; 21
Bovi-shield Gold FP5 5d- OLP: 0; 21
Bovi-shield Gold FP5 5d- OLP: 0; 21
Bovi-Shield Gold FP5 L5 - 10ds: 0; 21
Bovi-Shield Gold FP5 L5 - 50ds: 0; 21
Bovi-Shield Gold FP5 L5 - 5ds: 0; 21
Bovi-Shield Gold FP5 L5 - DOSE/10: 0; 21
Bovi-Shield Gold FP5 L5 - DOSE/5: 0; 21
Bovi-Shield Gold FP5 L5 DOSE/50: 0; 21
Bovi-shield Gold FP5 L5 HB - 10d: 0; 21
Bovi-shield Gold FP5 L5 HB - 5d: 0; 21
Bovi-shield Gold FP5 L5 HB- 50 d: 0; 21
Bovi-shield Gold with One Shot - 10ds: 0; 21
Bovi-shield Gold with One Shot - 10ds: 0; 21
Bovi-shield Gold with One Shot - 50ds: 0; 21
Bovi-shield Gold with One Shot - 50ds: 0; 21
Bovi-shield Gold with One Shot - BOX/10ds: 0; 21
Bovi-shield Gold with One Shot - BOX/50ds: 0; 21
Bovi-shield IBRPI3 50ds: 0; 21
Cal-Boost Calcium - BOLUS: 0; 0
Cal-Boost Calcium - BOX/12: 0; 0
Cal-Boost Calcium - CASE/48: 0; 0
Cal-Boost Calcium Bolus: 0; 0
Cal-Boost Calcium Bolus: 0; 0
Cal-Boost Calcium Bolus - Box of 12: 0; 0
Cal-Boost Calcium Bolus - Case of 48: 0; 0
Calf Guard Oral  - TRAY/25: 0; 21
Calf Guard Oral  OLP: 0; 21
Calf Guard Oral  OLP: 0; 21
Cattlemaster FP5+L5 25d OLP: 0; 21
Cattlemaster Gold FP5 10d - OLP: 0; 21
Cattlemaster Gold FP5 25d - OLP: 0; 21
Cefa Dri - BAG/24: 3.5; 42
Cefa Dri - PAIL/144: 3.5; 42
Cefa Dri (Bag/24): 3.5; 42
Cefa Dri Pail: 3.5; 42
Cefa Dri pail: 3.5; 42
Cefa Lak - BOX/24: 3; 2
Cefa Lak BOX/24: 3; 2
Cevaxel RTU 100ml: 0; 2
Cevaxel RTU 100ml - BOTTLE: 0; 2
Cevaxel RTU 250ml: 0; 2
Cevaxel RTU 250ml - BOTTLE: 0; 2
CIDR 330 Sheep/goat - each: 0; 0
Clean Up II - 0.5 Gal:	0; 0
Clean Up II - 9.5L (2.5gal): 0; 0
Coxevac 40ml: 0; 21
Cylence Pour On - 3L JUG: 0; 1
Cylence Pour On 3L: 0; 1
Cylence Pour On 3L: 0; 1
Cylence Pour On 500ml: 0; 1
Cylence Pour On 500ml: 0; 1
Cylence Pour On 500ml - BOTTLE: 0; 1
Cylence Pour On Applicator: 0; 1		
Dry Clox Bag/24: 30; 30
Dry Clox Pail/144: 30; 30
Dry Clox Pail/144: 30; 30
Dry Clox VP - 24 syringes: 30; 30
Dystosel 100 ml: 0; 21
Endovac-bovi (20 ds) 40ml: 0; 21
Endovac-bovi (20 ds) 40ml: 0; 21
Endovac-bovi (50d) 100ml: 0; 21
Endovac-bovi (50d) 100ml: 0; 21
Endovac-bovi 100ml - DOSE/50: 0; 21
Endovac-bovi 40ml - DOSE/20: 0; 21
Enviracor - 20ds: 0; 21
Enviracor 20d bottle - OLP: 0; 21
Enviracor Bottle - DOSE/20: 0; 21
Eprinex 2.5L: 0; 0
Eprinex 5 L - OLP: 0; 0
Estroplan 100ml: 0; 0
Estroplan 100ml: 0; 0
Estroplan 100ml - BOTTLE: 0; 0
Estroplan 20ml: 0; 0
Estroplan 20ml: 0; 0
Estroplan 20ml BOTTLE: 0; 0
Estroplan 50ml: 0; 0
Estroplan 50ml: 0; 0
Estroplan 50ml - BOTTLE: 0; 0
Estrumate 100ml: 0; 0
Estrumate 20ml: 0; 0
Excede 100mg 100ml: 0; 13
Excede 100mg 100ml BOTTLE: 0; 13
Excede 200mg 100ml: 0; 13
Excede 200mg 100ml - BOTTLE: 0; 13
Excenel 4g per ml: 0; 0
Excenel 4g per ml (included): 0; 0
Express FP10 - 10ds: 0; 21
FerAppease  300ml Applicator		
Fermicon 7-Somnugen - 10ds		
Fermicon 7-Somnugen - 50ds		
Fertiline 20mL - OLP: 0; 0
Fertiline 50mL - OLP: 0; 0
First Defense Boluses - BOX/30: 0; 0
First Defense Boluses - BOX/5: 0; 0
First Defense Box 30 Boluses: 0; 0
First Defense Box 30 Boluses: 0; 0
First Defense Box 5 Boluses- OLP: 0; 0
First Defense Box 5 Boluses- OLP: 0; 0
First Defense Dual Force (12 syr): 0; 0
First Defense Tri-Shield 12 x 30: 0; 0
Gonabreed 100ml: 0; 0
Gonabreed 100ml: 0; 0
Gonabreed 100ml - BOTTLE: 0; 0
Gonabreed 20ml: 0; 0
Gonabreed 20ml: 0; 0
Gonabreed 20ml - BOTTLE: 0; 0
Halocur 490ml - BOTTLE: 0; 20
Halocur 490ml bottle: 0; 20
Halocur 490ml bottle: 0; 20
Halocur 980ml - BOTTLE: 0; 20
Halocur 980ml Bottle: 0; 20
Halocur 980ml Bottle: 0; 20
Hiprabovis Somni/LKT - 10ds: 0; 21
Inforce - 50ds: 0; 21
Inforce - DOSE/10: 0; 21
Inforce 10ds: 0; 21
Inforce 3 - 10 dose: 0; 21
Inforce 3 - 50 dose: 0; 21
Inforce 3 - Single dose OLP: 0; 21
Inforce 3 - Single dose OLP: 0; 21
Inforce 3 - TRAY/25: 0; 21
Inforce Cannulas 25/pkg: 0; 21
Inforce DOSE/50: 0; 21
Inmeva - 125ds: 0; 21
Inmeva - 25ds: 0; 21
Inmeva 5d		
Ivomec 200 ml: not for use; 49
Ivomec 50 ml: not for use; 50
Ivomec 500 ml: not for use; 51
Ivomec Eprinex Applicator Gun: not for use; 52
Ivomec Pour On 5L: not for use; 53
J Vac - 10ds: 0; 21
J Vac - 50ds: 0; 21
J Vac - DOSE/10: 0; 21
J Vac - DOSE/50: 0; 21
J Vac 10d - OLP: 0; 21
J Vac 50d - OLP: 0; 21
K Phos Booster: 0; 0
Ketoprofen V - 250ml: 0; 1
Ketoprofen V 100ml: 0; 1
Ketoprofen V 100ml - BOTTLE: 0; 1
Ketoprofen V 250ml - BOTTLE: 0; 1
Klebsiella Vetovax - 50ds: 0; 21
Klebsiella Vetovax - 50ds: 0; 21
Klebsiella Vetovax - BOTTLE/50ds: 0; 21	
Lockout - Bag/24: 0; 0
Lockout - BAG/24: 0; 0
Lockout - Pail/144: 0; 0
Lockout - Pail/144: 0; 0
Lockout - PAIL/144: 0; 0
Lutalyse 100ml - BOTTLE: 0; 2
Lutalyse 100mL - OLP: 0; 2
Lutalyse 100mL - OLP: 0; 2
Multi Vitamin Mineral Inj - 100ml: 0; 0
Multi Vitamin Mineral Inj - 100ml: 0; 0
Multi Vitamin Mineral Inj 100ml - BOTTLE: 0; 0
Multimin 90 - 100ml Cattle: 0; 0
Multimin 90 - 500ml Cattle: 0; 0
Once PMH IN - 10ds: 0; 21
Once PMH IN - 50ds: 0; 21
Once PMH IN 25x1ds: 0; 21
Once PMH SQ - 10d: 0; 21
Once PMH SQ - 50d: 0; 21
One Shot BVD - 10ds: 0; 21
Orbeseal - BAG/24: 0; 0
Orbeseal - Pail: 0; 0
Orbeseal - PAIL/144: 0; 0
Orbeseal BAG  OLP: 0; 0
Orbeseal PAIL - OLP: 0; 0
Pyramid + Presponse - 10ds: 0; 21
Pyramid + Presponse - 50ds: 0; 21
Pyramid + Presponse - DOSE/10: 0; 21
Pyramid + Presponse - DOSE/50: 0; 21
Pyramid FP5 + Presponse - 50ds: 0; 21
Pyramid FP5 + Presponse 10d: 0; 21
Pyramid FP5 10d: 0; 21
Rabies - Cattle: 0; 21
Scour Bos 9 10d: 0; 21
Scour Bos 9 50d: 0; 21
Scourguard 4KC 10d -20ml OLP: 0; 21
Scourguard 4KC 50d-100mL - OLP: 0; 21
Scourguards 4KC - 10ds: 0; 21
Scourguards 4KC - 50ds: 0; 21
Scourguards 4KC - DOSE/10: 0; 21
Scourguards 4KC - DOSE/50: 0; 21
Selon E - 100ml: 0; 0
Selon E - 100ml: 0; 0
Selon E 100ml - BOTTLE: 0; 0
Spectramast DC - BAG/12: 30; 16
Spectramast DC - PAIL/144: 30; 16
Spectramast DC BAG of 12 from pail -OLP: 30; 16
Spectramast DC Pail 144 syringes: 30; 16
Spectramast DC PAIL- 144/Pail - OLP: 30; 16
Spectramast LC - Pail/144: 30; 16
Tasvax 8 - 100ml box: 0; 21
Tasvax 8 - 100ml box: 0; 21
Tasvax 8 - 250ml box: 0; 21
Tasvax 8 - 250ml box: 0; 21
Tasvax 8 - 500ml box: 0; 21
Tasvax 8 - 500ml box: 0; 21
Tasvax 8 - 50ml box: 0; 21
Tasvax 8 - 50ml box: 0; 21
Tasvax 8 - BOX/100ml: 0; 21
Tasvax 8 - BOX/250ml: 0; 21
Tasvax 8 - BOX/500ml: 0; 21
Tasvax 8 - BOX/50ml: 0; 21
Topvac Bacterin - DOSE/25: 0; 21
Topvac Bacterin - DOSE/5: 0; 21
Topvac Bacterin 25d: 0; 21
Topvac Bacterin 25d: 0; 21
Topvac Bacterin 5d: 0; 21
Topvac Bacterin 5d: 0; 21
Transition Bolus: 0; 0
Transition Bolus Box of 6: 0; 0
Transition Bolus Case of 48: 0; 0
Triangle 10 - 50d: 0; 21
Triangle 10 - BOTTLE/10ds: 0; 21
Triangle 10 - BOTTLE/50ds: 0; 21
Triangle 10 10d: 0; 21
Triangle 10 10d: 0; 21
Triangle 10 50d: 0; 21
Triangle 5 - 50ds: 0; 21
Triangle 5 10d: 0; 21
A180 Sterile sol'n 100ml: N/A; 7
Amprol 9.6% Soln 3.8 L: N/A; 7
Anafen 100mL BOTTLE: 0; 1
Atravet 10mg injectable (50ml): 2; 7
Banamine 100mL OLP: 1.5; 4
Baycox 5% 250ml - BOTTLE: N/A; 63
Baytril-100 cattle 100ml: N/A; 36
Borgal 100 ml: 4; 10		
Bovicam 100ml - BOTTLE: 4; 20
Bovicam 100ml - Bovine: 4; 20
Cal Mag Plus 500ml: 0; 0
Cal Mag Plus 500ml: 0; 0
Cal Mag Plus 500ml - BOTTLE: 0; 0
Cal Mag Plus 500ml - CASE: 0; 0
Cal Mag Plus 500ml CASE: 0; 0
Calboro  23% 500ml CASE OF 12: 0; 0
Calboro - 23% DMVet: 0; 0
Calboro - 23% DMVet: 0; 0
Calboro 23% 500ml - CASE/12: 0; 0
Calboro 23% DMVet - BOTTLE: 0; 0
Calcium propionate pwd 25kg: 0; 0
Calf Lyte II 10kg pail - OLP: 0; 0
Calf Lyte II 4kg - PAIL: 0; 0
Calf Lyte II 4kg pail - OLP: 0; 0
Calf Lyte II 4kg pail - OLP: 0; 0
Calf Lyte II 76g - CASE/24: 0; 0
Calf Lyte II 76g - PACKAGE: 0; 0
Calf Lyte II 76g pkg - OLP: 0; 0
Calf Lyte II 76g pkg - OLP: 0; 0
Calfspan Bolus - BOX/50: N/A; 28
Cefa Lak Box of 24: 4; 4
Cevaxel RTU - 100ml: 0; 3
Cevaxel RTU - 250ml: 0; 3	
Depocillin 100ml: 4; 10	
Dexamethasone 2mg (100ml) - BOTTLE: 2; 7		
Dextrose 50% - 500 ml  DMVet: 0; 0	
Draxxin 100ml - BOTTLE: N/A; 44
Draxxin KP - 100ml: N/A; 49
Duplocillin LA 100ml: N/A;	14		
Excede 200mg OL: 0; 13
Excenel 4g   box: 0; 0
Excenel 4g - BOX	
Excenel 4g OLP		
Forcyl Inj 100ml: N/A; 7	
Hymatil 100ml: N/A; 28		
Ketoprofen V - 100ml: 0; 1
Lice-Away Pour-On 4L: 0; 1
Lidocaine HCL 2% - 250ml: 0; 5		
Liquamycin LA 250 ml: N/A; 28 days IM, 48 days SQ
Meloxicam Oral - 4L: N/A; 35
Metricure (w/o catheters) - BOX/12: 0; 2
Micotil 100ml - OLP: N/A; 28		
Naquasone Inj - BOTTLE/100ml: 2; 21		
Nuflor 100ml - BOTTLE: N/A; 36 days IM, 55 days SQ
Oxyto-Sure 100ml - BOTTLE: 0; 4
Polyflex - 25g: 2; 6
Resflor 100mL: 0; 60	
Rompun 100mg 50ml: 2; 3	
Safeguard 10% susp. 3.78L: 0; 10
Safeguard Suspension 10%		
Saline 0.9% 1L bottle		
Salix (Lasix) Injectable: 2; 2		
Spectramast LC - BAG/12: 4; 0		
Thiamine 100ml (BVR): 0; 0		
Zeleris - 100ml: 0; 56
Zuprevo 100ml: 0; 42
Zuprevo 250ml`,
};

const ReptileTemplateSoapTemplateExample = {
  name: "Reptile Template",
  [TEMPLATE_FIELDS.SUBJECTIVE]: `Length of time owned:
Where acquired? □ Breeder 	□ Pet store 	□ Wild caught	□ Other:
How often is the reptile handled? □ Daily	□ Occasionally	□ Never
Last shed?:
Frequency of shedding?:
Any trouble shedding?: □ yes	□ no		
Do you bathe them?:
Any other reptiles?: □ yes	□ no	, if yes species:
If there are other reptiles, are they Housed together?: □ yes	□ no
Any contact with other reptile in the past 6 months?: □ yes	□ no
Any new reptile?: □ yes	□ no		If yes, was it quarantined?: □ yes	□ no
Any other pets?: □ yes □ no	, species:
Sick animals / deaths recently in the collection:
Husbandry
Type of enclosure:			
Size, dimensions, material of enclosure:
Where is enclosure located?
Cage substrate?:
If aquatic, is the water heated, how often water is changed, is water testing performed?
Frequency/method of cleaning?:		
Disinfectant?:
What is your tanks temperature minimum?:	Maximum?:		Basking area?:
Temperature at night?:
How is the temperature measured?:
Heat source:  □ ceramic, power □ spot bulb, power □ infrared bulb
What is the distance of the heat source from the animal?:
Heating mat?: □ yes	□ no,     if yes, is it □ inside cage □ under cage
Heating rock?: □ yes	□ no
Other heaters used:
Can the animal touch or access the heat source?: □ yes	□ no
Humidity level?:		
How is humidity measured?:
What is the light cycle?:			
Is the enclosure close to a window?:
Is there additional lighting in the enclosure?:
UVB: □ yes	□ no □ Fluorescent	□ Mercury vapor bulb
Brand and type of UVB:
When was the bulb purchased?		
How often is the bulb changed?
What is the distance of the UVB from the animal?:
Anything between the UVB source and the animal? □ yes	□ no
Any exposure to the sun? 	□ yes	□ no
Any changes in the reptile environment/enclosure in past 3 months: □ yes	□ no
Nutrition
Type of food offered?:
Amount fed/frequency?:			
When was the patient last fed?
Are they fed insects? □ yes	□ no , if yes are they dusted or gut loaded?:
Are they fed mice? □ yes	□ no , source, how thawed, live or dead?
Supplements: □ yes	□ no,  if yes what types:
Water source?: 		
How often is the water changed?
How is water provided?
Past Medical History:
(If female, include reproductive history (including problems))
Presenting Problems:
(include presenting complaint, description, duration, progression, previous treatments)
Other Information
Patient's general attitude?: □ normal	□ abnormal , specify:
Any changes in appetite / eating behavior?: □ yes	□ no, specify:
Any changes in drinking?: □ yes 	□ no
Any changes in feces?: □ yes 	□ no, specify:
Any breathing problems?: □ yes	□ no, specify:
Is the reptile on any current medications?: □ yes	□ no, specify:`,
  [TEMPLATE_FIELDS.OBJECTIVE]: `Eyes:
Ears:
Beak / oral cavity( including choanal papillae):
Nares:
Neck:
Abdomen/ coelomic cavity:
Heart and lungs:
Limbs:
Integument/ scales/ shell:
Urogenital:
Neurological:`,
  [TEMPLATE_FIELDS.ASSESSMENT]: `Assessment: Give the case status (example: patient is in currently stable but at risk of sudden decompensation)
Include differential diagnoses under subtitle (DDX:)`,
  [TEMPLATE_FIELDS.PLAN]: `Client communication with consent for diagnostics, if price is mentioned please include it:
Diagnostics and outcomes:
Recommended treatments:`,
};

const PuppyDischargeFreeformTemplateExample = {
  name: "Puppy Discharge",
  [TEMPLATE_FIELDS.CUSTOM]: `**Puppy Discharge Instructions**

Patient:
Breed:
Age:
Weight:
Owner:
Visit Date:

**Visit Summary:**

Vaccinations Given:
[List of Vaccines Administered]
**Next Due:** [Next Vaccines Due]

Parasite Control Plan: (Leave off if not discussed)

Medications: (Leave off if not discussed)

Nutrition Plan: (Leave off if not discussed)

**Follow-Up:**
{{NextVisitDate}} - {{NextVisitReason}}

Thank you for trusting {{PracticeName}} with your puppy's care.`,
};

const ClientFollowUpEmailFreeformTemplateExample = {
  name: "Client Follow-Up Email",
  [TEMPLATE_FIELDS.CUSTOM]: `You are a veterinarian composing a personalized email to a client you recently saw, summarizing key details from the appointment. Include personal information about the client and their pet. Make sure the email is clear and easy to understand for the general audience, using medical terms only when necessary. Keep the tone friendly, professional, and brief.`,
};

const KittenDischargeFreeformTemplateExample = {
  name: "Kitten Discharge",
  [TEMPLATE_FIELDS.CUSTOM]: `**Kitten Discharge Instructions**

Patient:

Breed:

Age:

Weight:

Owner:

Visit Date:

**Visit Summary:**

**Vaccinations Given:** [List of Vaccines Administered]

**Next Due:** [Next Vaccines Due]

**Parasite Control Plan:** (Leave off if not discussed)

**Medications:** (Leave off if not discussed)

**Nutrition Plan:** (Leave off if not discussed)

**Follow-Up:** {{NextVisitDate}} - {{NextVisitReason}}

Thank you for trusting {{PracticeName}} with your kitten's care.`,
};

const InjuryDischargeFreeformTemplateExample = {
  name: "Injury Discharge",
  [TEMPLATE_FIELDS.CUSTOM]: `***What Happened Today*** [Patient name] was seen today for [injury type and affected area as described by veterinarian]. [Insert 1-2 sentence plain-language summary of diagnosis and what it means for the pet's recovery.]


***Medications**  - Please Give Exactly as Directed* [List each medication mentioned during the appointment in the format below. If no medications were prescribed, omit this section.]

[Medication name]  - [dose as stated by veterinarian]  - give [frequency] for [duration]. Do not stop medications early, even if [patient name] seems better. If you miss a dose or have trouble giving medication, call us at [clinic phone number if mentioned, otherwise leave blank for front desk to complete].


***Step 1: Cage Rest*** [Patient name] needs complete rest for [number of weeks as stated by veterinarian]. During this time, [patient name] must be confined to a crate or small pen at all times except for bathroom breaks, kept on a leash for all outdoor time even in a fenced yard, and restricted from jumping, running, stairs, or rough play.

[If veterinarian mentions anxiety or restlessness as a concern, insert: If [patient name] is struggling to rest, contact us, calming medications may be prescribed to help.]


***Step 2: Gradual Return to Activity*** [Include this section only if the veterinarian discusses return to activity or follow-up exercise plan. If not discussed, omit.] After the rest period is complete, a recheck appointment will confirm [patient name] is ready to gradually increase activity. A typical return-to-exercise plan looks like this: leashed harness walks of 5-10 minutes for 2 weeks, increasing to 10-15 minutes for 2 weeks, then 15-20 minutes for 2 weeks. After completing all phases, [patient name] may return to normal off-leash activity. Do not skip or rush steps  - this risks reinjury.


***What to Watch For Every Day*** Monitor [patient name] daily for the following warning signs:

General signs of concern  - vomiting, diarrhea, not eating, unusual lethargy, or signs of pain. If any of these are noted, contact your veterinarian.

[If spinal, neurological, or mobility injury mentioned during appointment, insert: Watch closely for any changes in [patient name]'s ability to walk. Signs of worsening include dragging of the hind limbs, stumbling, weakness, or sudden inability to bear weight. If any of these occur, this is an emergency  - seek veterinary care immediately.]

[If veterinarian mentions any additional monitoring instructions specific to the injury type, insert those here in plain language matching the tone above.]


***Go to an Emergency Clinic Immediately If You Notice:*** [Populate this list based on injury type and any specific concerns raised by the veterinarian during the appointment. Always include the following as a baseline:] Inability to walk or sudden paralysis, dragging of hind limbs, uncontrolled bleeding, collapse, severe or rapidly worsening pain, open-mouth breathing or bluish gums, or any sudden and dramatic deterioration in condition. Do not wait to see if it improves.


***Your Next Appointment*** [If a recheck was scheduled or discussed during the appointment, include: Recheck scheduled for [timeframe or specific date if mentioned] with [veterinarian name or clinic name]. The purpose of this visit is to [assess healing / clear for return to activity / review medications  - use whichever apply based on appointment context].]

[If no recheck was discussed, insert: Please contact us to schedule a follow-up appointment at [clinic phone/email].]

If you have any questions before your next visit, we are always here to help  - no question is too small. Contact us at [clinic contact information].`,
};

const SelfReviewVisitCommunicationFreeformTemplateExample = {
  name: "Self-Review: Visit Communication",
  [TEMPLATE_FIELDS.CUSTOM]: `Communication Assessment: [Create an assessment of my communication skills as if I were a newly graduated veterinarian, and you are a highly experienced doctor coaching me on my communication, with a specific focus on the treatment plan discussion with the customer.]

1. Outline of recommendations and treatment plan conversation:
2. List of treatments recommended vs. declined:
3. Point out any places where recommendations were ambiguous or unclear:
4. Did the communication optimize for patient medical compliance? And if not, what could have been done differently?
5. Tips and improvement plan:`,
  preferences: `Use Plain text, do not use markdown or special characters. Focus on objective and actionable things. Keep it positive and constructive.`,
};

const InvoiceAuditChecklistFreeformTemplateExample = {
  name: "Invoice Audit Checklist",
  [TEMPLATE_FIELDS.CUSTOM]: `**Invoice Audit Checklist** Patient: Owner: Visit Date: Doctor:

**Items Recommended & Charged:** [List each item recommended during the appointment]

**Items Recommended but Declined:** [List any items that were recommended, but the customer declined]

**Medications Dispensed:** [List all medications sent home]

**Diagnostics Performed:** [List labs, imaging, or other diagnostics]

**Procedures Performed:** [List any procedures completed and the associated invoice items]

**Notes:** [Any discrepancies, clarifications, or follow-up needed]`,
};

// ==================== END TEMPLATE LIBRARY PRESET EXAMPLES ====================

// ==================== TEMPLATE EXAMPLE MAPPINGS ====================

export const PREFERENCES_PREFILLS = {
  "Capitalize Abnormals": {
    description: "Write abnormals in all capital letters.",
  },
  "Use Point Form": {
    description:
      "Write [add section here] in point form (use dashes).",
  },
  "Medical Terminology": {
    description: "Use medical terminology where possible.",
  },
  "Use °F for Temperature": {
    description: "Use Fahrenheit for temperature.",
  },
  "Structure Recommendations": {
    description:
      "Include all potential courses of treatments discussed with costs, even if declined (note if declined).",
  },
  "Too Verbose": {
    description: "Keep [add section here] under 50 words.",
  },
  Summarize: {
    description:
      "Summarize [add section here] within 50 - 100 words.",
  },
  "Paragraph form": {
    description: "Write [add section here] in paragraph form.",
  },
};

export const SOAP_TEMPLATE_EXAMPLES = {
  [`${DEFAULT_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    DEFAULT_SOAP_TEMPLATE_EXAMPLE,
  [`${ADULT_WELLNESS_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    ADULT_WELLNESS_SOAP_TEMPLATE_EXAMPLE,
  [`${MY_LUMPS_AND_BUMPS_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    MY_LUMPS_AND_BUMPS_SOAP_TEMPLATE_EXAMPLE,
  [`${MY_GENERAL_CONSULT_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    MY_GENERAL_CONSULT_SOAP_TEMPLATE_EXAMPLE,
  [`${MY_RECHECK_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    MY_RECHECK_SOAP_TEMPLATE_EXAMPLE,
  [`${ALLERGY_APPOINTMENT_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    ALLERGY_APPOINTMENT_SOAP_TEMPLATE_EXAMPLE,
  [`${DR_KATIES_GP_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    DR_KATIES_GP_SOAP_TEMPLATE_EXAMPLE,
  [`${DR_KATIES_MULTI_PET_SOAP_TEMPLATE_EXAMPLE["name"]}`]:
    DR_KATIES_MULTI_PET_SOAP_TEMPLATE_EXAMPLE,
};

export const FREEFORM_TEMPLATE_EXAMPLES = {
  [`${MY_ER_ROUNDS_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_ER_ROUNDS_FREEFORM_TEMPLATE_EXAMPLE,
  [`${MY_STANDARD_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_STANDARD_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE,
  [`${MY_RADIOLOGY_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_RADIOLOGY_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE,
  [`${MY_CASE_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_CASE_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE,
  [`${MY_STRUCTURED_CALLBACK_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_STRUCTURED_CALLBACK_FREEFORM_TEMPLATE_EXAMPLE,
  [`${MY_STRUCTURED_SURGERY_OVH_FELINE_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_STRUCTURED_SURGERY_OVH_FELINE_FREEFORM_TEMPLATE_EXAMPLE,
  [`${MY_SIMPLE_CLIENT_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_SIMPLE_CLIENT_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE,
  [`${MY_DISCHARGE_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE["name"]}`]:
    MY_DISCHARGE_SUMMARY_FREEFORM_TEMPLATE_EXAMPLE,
};

export const SOAP_TEMPLATE_LIBRARY_EXAMPLES = {
  [`${GeneralPracticeSoapTemplateExample["name"]}`]:
    GeneralPracticeSoapTemplateExample,
  [`${GeneralIllnessTemplateSoapTemplateExample["name"]}`]:
    GeneralIllnessTemplateSoapTemplateExample,
  [`${ErTemplateSoapTemplateExample["name"]}`]:
    ErTemplateSoapTemplateExample,
  [`${CardiologySoapTemplateExample["name"]}`]:
    CardiologySoapTemplateExample,
  [`${LargeAnimalEquineGeneralSoapTemplateExample["name"]}`]:
    LargeAnimalEquineGeneralSoapTemplateExample,
  [`${ReptileTemplateSoapTemplateExample["name"]}`]:
    ReptileTemplateSoapTemplateExample,
};

export const FREEFORM_TEMPLATE_LIBRARY_EXAMPLES = {
  [`${ReviewingResultsFreeformTemplateExample["name"]}`]:
    ReviewingResultsFreeformTemplateExample,
  [`${CanineDentalFreeformTemplateExample["name"]}`]:
    CanineDentalFreeformTemplateExample,
  [`${FelineDentalFreeformTemplateExample["name"]}`]:
    FelineDentalFreeformTemplateExample,
  [`${StaffMeetingFreeformTemplateExample["name"]}`]:
    StaffMeetingFreeformTemplateExample,
  [`${HospitalManagerAndEmployee1On1TemplateExample["name"]}`]:
    HospitalManagerAndEmployee1On1TemplateExample,
  [`${CprFreeformTemplateExample["name"]}`]:
    CprFreeformTemplateExample,
  [`${InpatientFreeformTemplateExample["name"]}`]:
    InpatientFreeformTemplateExample,
  [`${CanineNeuterFreeformTemplateExample["name"]}`]:
    CanineNeuterFreeformTemplateExample,
  [`${CanineSpayFreeformTemplateExample["name"]}`]:
    CanineSpayFreeformTemplateExample,
  [`${FelineNeuterFreeformTemplateExample["name"]}`]:
    FelineNeuterFreeformTemplateExample,
  [`${FelineSpayFreeformTemplateExample["name"]}`]:
    FelineSpayFreeformTemplateExample,
  [`${DetailedAbdominalUltrasoundFreeformTemplateExample["name"]}`]:
    DetailedAbdominalUltrasoundFreeformTemplateExample,
  [`${LargeAnimalOntarioMilkAndMeatWithdrawalTimesTemplateExample["name"]}`]:
    LargeAnimalOntarioMilkAndMeatWithdrawalTimesTemplateExample,
  [`${PuppyDischargeFreeformTemplateExample["name"]}`]:
    PuppyDischargeFreeformTemplateExample,
  [`${ClientFollowUpEmailFreeformTemplateExample["name"]}`]:
    ClientFollowUpEmailFreeformTemplateExample,
  [`${KittenDischargeFreeformTemplateExample["name"]}`]:
    KittenDischargeFreeformTemplateExample,
  [`${InjuryDischargeFreeformTemplateExample["name"]}`]:
    InjuryDischargeFreeformTemplateExample,
  [`${SelfReviewVisitCommunicationFreeformTemplateExample["name"]}`]:
    SelfReviewVisitCommunicationFreeformTemplateExample,
  [`${InvoiceAuditChecklistFreeformTemplateExample["name"]}`]:
    InvoiceAuditChecklistFreeformTemplateExample,
};

export const TEMPLATE_OPTIONS_MAP = {
  [TEMPLATE_TYPES.SOAP]: Object.values(SOAP_TEMPLATE_EXAMPLES).map(
    (template) => template.name,
  ),
  [TEMPLATE_TYPES.CUSTOM]: Object.values(
    FREEFORM_TEMPLATE_EXAMPLES,
  ).map((template) => template.name),
};

// ==================== TEMPLATE EXAMPLE MAPPINGS ====================

// ==================== TRANSCRIPT SAMPLE MAPPINGS ====================

const defaultTranscriptSample = {
  name: "Default",
  sampleTranscript: `Julius:
Eating well (on c/d diet)
Normal litter box habits

Mild ear debris, no otitis
Clear eyes, healthy teeth
Overweight (Body condition 7/9)
Normal lymph nodes
Normal heart rate
Comfortable abdomen
Normal mobility

Healthy cat with minor weight management needs.

Reduce c/d diet portion size due to high caloric content
Continue dental care attempts despite difficulty

Three-year rabies vaccine (right hind leg, subcutaneous)
Three-year feline viral vaccine (left hind leg, subcutaneous)
Nail trim
Lickable treat provided`,
};

export const TRANSCRIPT_SAMPLE_MAPPINGS = {
  DEFAULT: { ...defaultTranscriptSample },
  [TEMPLATE_TYPES.SOAP]: Object.values(SOAP_TEMPLATE_EXAMPLES).map(
    (template) => ({
      name: template.name,
      sampleTranscript: template.sampleTranscript,
    }),
  ),
  [TEMPLATE_TYPES.CUSTOM]: Object.values(
    FREEFORM_TEMPLATE_EXAMPLES,
  ).map((template) => ({
    name: template.name,
    sampleTranscript: template.sampleTranscript,
  })),
};
